import {
  Box,
  Flex,
  Icon,
  IconButton,
  Image,
  Link,
  Tooltip,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Skeleton,
  useClipboard,
} from '@chakra-ui/react'
import React, { useRef, useState } from 'react'
import { Copy, Check, ExternalLink, Expand, Loader2, Building2, Package, ShieldAlert, Telescope, Building, Server, Shrink, Users, Gavel, Contact, Plus, Sparkles, Fingerprint, Image as ImageIcon, ScanSearch, Radar } from 'lucide-react'
import { SellersIcon, PackageBox, Globe, PlatformLayout, Email, Phone } from '../../../utils/constants/icons'

import {
  Edge,
  Node,
  Handle,
  Position,
  BaseEdge,
  EdgeProps,
  getBezierPath,
  NodeToolbar,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'

import type { KrakenGraphResponse, KrakenNode, KrakenNodeLabel } from '../../../types/kraken.types'
import DiscoverWebPopover from './DiscoverWebPopover'

const DEFAULT_NODE_SIZE = 56

// Pretty-print a raw phone number into readable groups, e.g.
// "+905445444438" → "+90 544 544 4438". Leaves non-phone strings untouched.
const formatPhoneLabel = (raw: string): string => {
  const s = String(raw).trim()
  const digits = s.replace(/\D/g, '')
  if (digits.length < 8 || digits.length > 15) return s
  const cc = s.startsWith('+') ? digits.slice(0, 2) : ''
  const rest = cc ? digits.slice(2) : digits
  const chunks = rest.match(/\d{1,3}/g) || []
  if (chunks.length > 1 && chunks[chunks.length - 1].length === 1) {
    chunks[chunks.length - 2] += chunks.pop()
  }
  return (cc ? `+${cc} ` : '') + chunks.join(' ')
}

// Helper function to shorten titles while preserving readability
const shortenTitle = (title: string, maxLength: number = 6): string => {
  if (!title) return ''
  if (title.length <= maxLength) return title

  if (title.includes('.') && (title.includes('http') || title.includes('www'))) {
    const urlParts = title
      .replace(/https?:\/\//, '')
      .replace(/www\./, '')
      .split('/')
    const domain = urlParts[0].split('.')[0]
    return domain.substring(0, maxLength)
  }

  const firstWord = title.split(' ')[0]
  if (firstWord.length <= maxLength) return firstWord

  return title.substring(0, maxLength) + '...'
}

export type CircleNodeData = {
  label: string
  fullTitle: string
  firstLetter: string
  kind: 'SELLER' | 'ITEM' | 'SAW' | 'LINK' | 'IMAGE' | 'SOCIAL' | 'EMAIL' | 'PHONE' | 'PLATFORM' | 'ENTITY' | 'PRODUCT' | 'SEIZURE' | 'REGISTRAR' | 'ISP' | 'REGISTRANT' | 'CASE' | 'SUBCASE' | 'CONTACT' | 'GROUP'
  url?: string
  original?: KrakenNode
  active?: boolean
  /** GROUP nodes only: bundle of >5 same-relation neighbours. */
  count?: number
  relation?: string
  memberKind?: CircleNodeData['kind']
  collapsed?: boolean
  /** Toggle a GROUP node open/closed (injected by KrakenNetworkTree). */
  onToggleGroup?: () => void
  /** Set on members packed into an expanded group's tight cluster — hides the
   *  label so the dense cluster stays readable. */
  clustered?: boolean
  /** Set transiently while /kraken/expand/ is streaming for this vertex. */
  expanding?: boolean
  /** Pre-bound expand callback injected by KrakenNetworkTree (closes over the node id). */
  onExpand?: () => void
  /** Pre-bound "view detail page" navigation, set when the node has a route in Ocean. */
  onNavigate?: () => void
  /** Set transiently while /kraken/discover-web/ is streaming for this vertex. */
  discovering?: boolean
  /** Pre-bound web-presence discovery callback (Sonnet-scored Firecrawl search). */
  onDiscoverWeb?: (opts: { layers: number; categories?: string[] }) => void
  /** Pre-bound reverse-image-search callback for nodes carrying an image
   *  (the searched-image node, product thumbnails, social-post screenshots).
   *  Receives the image URL to search. */
  onReverseImageSearch?: (imageUrl: string) => void
  /** Pre-bound OSINT callbacks for identifier nodes: check which online services
   *  an email is registered on / look up a phone's carrier + line type. The
   *  button only renders when the node's value is a recognisable email/phone. */
  onInvestigateEmailOsint?: (email: string) => void
  onInvestigatePhoneOsint?: (phone: string) => void
  /** Pre-bound username-OSINT callback: check where a username/handle exists
   *  across ~700 sites. Shown on social-profile nodes, @handle values, and
   *  seller/entity name nodes. Receives the username. */
  onInvestigateUsernameOsint?: (username: string) => void
  /** Pre-bound domain-footprint callback: subdomains + co-hosted siblings +
   *  look-alike domains. Shown on SAW / domain-bearing nodes (not infra).
   *  Receives the domain. */
  onDomainFootprint?: (domain: string) => void
  /** Pre-bound callback to create a new custom entity pre-linked to this node
   *  (in-Ocean records → opens AddEntityModal / "Create custom link"). */
  onCreateEntity?: () => void
  /** Pre-bound callback for a discovered LINK node NOT yet in Ocean — asks the
   *  AI agent to scrape it, reason the type, and create a CustomEntity. */
  onAiCreateEntity?: () => void
  /** Set transiently while the direct create-entity flow is running for this
   *  LINK node — drives the "+" button's spinner + disabled state. */
  creatingEntity?: boolean
  /** True when this LINK's domain already maps to a CustomEntity (backend-stamped
   *  on retrieve, or set optimistically after an in-session create). Hides the
   *  "Create entity with AI" button so we don't offer to re-create it. */
  entityExists?: boolean
  /** Export mode: when set, shows a numbered badge and suppresses the hover popover. */
  exportNum?: number
}

const formatDate = (v: unknown): string | undefined => {
  if (v == null || v === '') return undefined
  const d = new Date(v as string)
  if (Number.isNaN(d.getTime())) return undefined
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const formatPrice = (original: any): string | undefined => {
  if (original?.price_eur != null) return `€${Number(original.price_eur).toFixed(2)}`
  if (original?.price_usd != null) return `$${Number(original.price_usd).toFixed(2)}`
  if (original?.price != null) {
    const c = typeof original.currency === 'string' ? original.currency : original.currency?.code ?? ''
    return `${Number(original.price).toFixed(2)} ${c}`.trim()
  }
  return undefined
}

function FieldRow({
  label,
  value,
  copy,
  href,
  mono,
}: {
  label: string
  value: React.ReactNode
  copy?: string
  href?: string
  mono?: boolean
}) {
  const copyValue = copy ?? (typeof value === 'string' ? value : '')
  const { hasCopied, onCopy } = useClipboard(copyValue)
  // Also treat whitespace-only strings as empty so we don't render an
  // invisible row (which shows up as a thin gray bar between real rows
  // because the VStack still draws its divider around the "blank" child).
  const isBlank =
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'string' && value.trim() === '')
  if (isBlank) return null
  return (
    <HStack align="start" w="100%" spacing={3} py="3px">
      <Text fontSize="11px" color="gray.500" minW="78px" pt="2px" fontWeight={500} letterSpacing="0.01em">
        {label}
      </Text>
      <HStack flex="1" spacing={1.5} align="center" minW={0}>
        {href ? (
          <Link
            href={href}
            isExternal
            fontSize="12.5px"
            color="purple.600"
            noOfLines={2}
            wordBreak="break-all"
            onClick={(e) => e.stopPropagation()}
            _hover={{ color: 'purple.700', textDecoration: 'underline' }}
          >
            {value} <Icon as={ExternalLink} w="10px" h="10px" mb="2px" />
          </Link>
        ) : (
          <Text
            fontSize="12.5px"
            fontFamily={mono ? 'mono' : undefined}
            color="gray.800"
            noOfLines={3}
            wordBreak="break-word"
            fontWeight={500}
          >
            {value}
          </Text>
        )}
        {copyValue && (
          <IconButton
            aria-label="Copy"
            icon={<Icon as={hasCopied ? Check : Copy} w="11px" h="11px" />}
            size="xs"
            variant="ghost"
            minW="18px"
            h="18px"
            color={hasCopied ? 'green.500' : 'gray.400'}
            onClick={(e) => {
              e.stopPropagation()
              onCopy()
            }}
          />
        )}
      </HStack>
    </HStack>
  )
}

function StatusBadge({ status }: { status?: string }) {
  if (!status) return null
  const s = String(status)
  const scheme =
    s === 'COMPLETED'
      ? 'green'
      : s === 'RUNNING'
        ? 'blue'
        : s === 'ERROR'
          ? 'red'
          : s.includes('INFRINGING')
            ? 'orange'
            : 'gray'
  return (
    <Badge size="sm" colorScheme={scheme} fontSize="10px">
      {s}
    </Badge>
  )
}

// ─── Generic field rendering ────────────────────────────────────────────────
// Graph/layout/internal keys that should never surface as a field row.
const INTERNAL_FIELD_KEYS = new Set<string>([
  'id', 'label', 'entity_type', 'name_lower', 'is_terminating', 'weight',
  'source', 'degree', 'depth', 'verified', 'index', 'screenshot',
  'x', 'y', 'vx', 'vy', 'fx', 'fy', 'latitude', 'longitude',
  'thumbnail', 'image', 'photo', 'incoming_domains', 'outgoing_domains',
  // duplicate id mirrors of the vertex id / internal foreign keys
  'item_id', 'saw_id', 'seller_id', 'platform_id', 'brand_id', 'brand_ids',
  'member_id', 'registrar_id', 'registrant_id', 'isp_id', 'entity_id',
  'product_id', 'seizure_id', 'website_id',
])

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  url: 'URL', sku: 'SKU', asin: 'ASIN', tax_id: 'Tax ID', isp: 'ISP',
  case_number: 'Case number', created_at: 'Created', updated_at: 'Updated',
}

const humanizeFieldKey = (key: string): string => {
  if (FIELD_LABEL_OVERRIDES[key]) return FIELD_LABEL_OVERRIDES[key]
  return key
    .replace(/_/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => (w === 'id' ? 'ID' : w === 'url' ? 'URL' : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ')
}

const DATE_KEY_RE = /(_at|_date|_on)$|^(created|updated|seized|imported)$/

// Coerce a raw property value into a human string, or undefined when it carries
// no useful display value (nested objects, empty arrays, blanks).
const formatFieldValue = (key: string, value: unknown): string | undefined => {
  if (value === null || value === undefined || value === '') return undefined
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) {
    const parts = value.filter((v) => v != null && typeof v !== 'object').map(String)
    return parts.length ? parts.join(', ') : undefined
  }
  if (typeof value === 'object') return undefined
  if (DATE_KEY_RE.test(key)) return formatDate(value) ?? String(value)
  const s = String(value)
  return s.trim() === '' ? undefined : s
}

// Renders every meaningful key on `original` that isn't internal or already
// shown by a curated body. Used as the full body for member-portal records
// (whose schema is external/variable) and as an "additional details" appendix
// for other node kinds so nothing the payload carries stays hidden.
function GenericFieldsBody({
  original,
  skipKeys,
  priority = [],
  caption,
  leadDivider,
}: {
  original: any
  skipKeys?: Set<string>
  priority?: string[]
  caption?: string
  leadDivider?: boolean
}) {
  if (!original || typeof original !== 'object') return null
  const rows = Object.keys(original)
    .filter((k) => !INTERNAL_FIELD_KEYS.has(k) && !skipKeys?.has(k))
    .map((k) => ({ key: k, label: humanizeFieldKey(k), value: formatFieldValue(k, original[k]) }))
    .filter((r): r is { key: string; label: string; value: string } => r.value !== undefined)
  if (!rows.length) return null
  const rank = (k: string) => {
    const i = priority.indexOf(k)
    return i === -1 ? priority.length : i
  }
  rows.sort((a, b) => rank(a.key) - rank(b.key) || a.label.localeCompare(b.label))
  return (
    <>
      {leadDivider && <Divider borderColor="gray.100" my={1.5} />}
      {caption && (
        <Text fontSize="9px" color="gray.400" textTransform="uppercase" letterSpacing="0.06em" mb={1}>
          {caption}
        </Text>
      )}
      <VStack align="stretch" spacing={0} divider={<Divider borderColor="gray.100" />}>
        {rows.map((r) => {
          const raw = original[r.key]
          const isUrl = typeof raw === 'string' && /^https?:\/\//.test(raw)
          return (
            <FieldRow
              key={r.key}
              label={r.label}
              value={r.value}
              href={isUrl ? raw : undefined}
              copy={typeof raw === 'string' ? raw : undefined}
            />
          )
        })}
      </VStack>
    </>
  )
}

// Keys each curated <KindPopoverBody> already renders — the generic appendix
// excludes these so it only surfaces *additional* fields the node carries.
const CURATED_BODY_KEYS: Record<string, string[]> = {
  ITEM: ['title', 'url', 'price', 'price_eur', 'price_usd', 'currency', 'domain', 'platform', 'brand', 'seller', 'sku', 'asin', 'country', 'product_type', 'infringement_type', 'created_at'],
  SELLER: ['name', 'url', 'platform', 'seller_type', 'type', 'email', 'phone', 'country', 'city', 'tax_id', 'repeat_infringer', 'flagged_items', 'removed_items', 'description', 'marked_as_infringing_at', 'created_at'],
  SAW: ['domain', 'title', 'url', 'site_type', 'infringement_type', 'country', 'language', 'registrar', 'registrant', 'ranking', 'description', 'created_at'],
  SOCIAL: ['domain', 'title', 'url', 'site_type', 'infringement_type', 'country', 'language', 'registrar', 'registrant', 'ranking', 'description', 'created_at'],
  PLATFORM: ['name', 'title', 'domain', 'url', 'country', 'created_at'],
  ENTITY: ['name', 'type', 'nickname', 'url', 'email', 'phone', 'address', 'city', 'postcode', 'country', 'tax_id', 'description', 'created_at'],
  PRODUCT: ['name', 'product_type', 'price', 'url', 'seller', 'website', 'created_at'],
  SEIZURE: ['name', 'value', 'address', 'city', 'postcode', 'country', 'seized_at', 'remarks', 'created_at'],
  EMAIL: ['url', 'name'],
  PHONE: ['url', 'name'],
  LINK: ['url', 'name'],
}
const curatedBodyKeys = (kind: string): Set<string> => new Set(CURATED_BODY_KEYS[kind] ?? [])

function ItemPopoverBody({ original }: { original: any }) {
  const price = formatPrice(original)
  const thumbnail = original?.thumbnail || original?.image
  const platform = typeof original?.platform === 'string' ? original.platform : original?.platform?.name
  const brand = typeof original?.brand === 'string' ? original.brand : original?.brand?.name
  const seller = typeof original?.seller === 'string' ? original.seller : original?.seller?.name
  const created = formatDate(original?.created_at)

  return (
    <VStack align="stretch" spacing={0} w="100%">
      {thumbnail && (
        <Box mx={-4} mt={-4} mb={3} position="relative" overflow="hidden" borderTopRadius="lg">
          <Image
            src={thumbnail}
            alt={original?.title}
            w="100%"
            maxH="160px"
            objectFit="cover"
            fallback={<Box h="0" />}
          />
          {price && (
            <Box
              position="absolute"
              top="10px"
              right="10px"
              bg="rgba(17, 24, 39, 0.85)"
              color="white"
              fontSize="12px"
              fontWeight={600}
              px={2.5}
              py={1}
              borderRadius="md"
              backdropFilter="blur(4px)"
            >
              {price}
            </Box>
          )}
        </Box>
      )}

      <VStack align="stretch" spacing={0} divider={<Divider borderColor="gray.100" />}>
        <FieldRow label="Title" value={original?.title} copy={original?.title} />
        <FieldRow label="URL" value={original?.url} href={original?.url} copy={original?.url} />
        {!thumbnail && price && <FieldRow label="Price" value={price} copy={price} />}
        <FieldRow label="Domain" value={original?.domain} copy={original?.domain} />
        <FieldRow label="Platform" value={platform} />
        <FieldRow label="Brand" value={brand} />
        <FieldRow label="Seller" value={seller} />
        <FieldRow label="SKU" value={original?.sku} copy={original?.sku} mono />
        <FieldRow label="ASIN" value={original?.asin} copy={original?.asin} mono />
        <FieldRow label="Country" value={original?.country} />
        <FieldRow label="Category" value={original?.product_type} />
        <FieldRow label="Infringement" value={original?.infringement_type} />
        <FieldRow label="Created" value={created} />
      </VStack>
    </VStack>
  )
}

function SellerPopoverBody({ original }: { original: any }) {
  const platform = typeof original?.platform === 'string' ? original.platform : original?.platform?.name
  const created = formatDate(original?.created_at)
  const flaggedAt = formatDate(original?.marked_as_infringing_at)
  return (
    <VStack align="stretch" spacing={0} divider={<Divider borderColor="gray.100" />}>
      <FieldRow label="Name" value={original?.name} copy={original?.name} />
      <FieldRow label="URL" value={original?.url} href={original?.url} copy={original?.url} />
      <FieldRow label="Platform" value={platform} />
      <FieldRow label="Type" value={original?.seller_type || original?.type} />
      <FieldRow label="Email" value={original?.email} copy={original?.email} href={original?.email ? `mailto:${original.email}` : undefined} />
      <FieldRow label="Phone" value={original?.phone} copy={original?.phone} href={original?.phone ? `tel:${original.phone}` : undefined} />
      <FieldRow label="Country" value={original?.country} />
      <FieldRow label="City" value={original?.city} />
      <FieldRow label="Tax ID" value={original?.tax_id} copy={original?.tax_id} mono />
      <FieldRow label="Repeat infringer" value={original?.repeat_infringer === true ? 'Yes' : undefined} />
      <FieldRow label="Items flagged" value={original?.flagged_items} />
      <FieldRow label="Items removed" value={original?.removed_items} />
      <FieldRow label="Description" value={original?.description} />
      <FieldRow label="Flagged" value={flaggedAt} />
      <FieldRow label="Created" value={created} />
    </VStack>
  )
}

function SitePopoverBody({ original }: { original: any }) {
  const registrar =
    typeof original?.registrar === 'string' ? original.registrar : original?.registrar?.name
  const registrant =
    typeof original?.registrant === 'string' ? original.registrant : original?.registrant?.name
  const created = formatDate(original?.created_at)
  return (
    <VStack align="stretch" spacing={0} divider={<Divider borderColor="gray.100" />}>
      <FieldRow label="Domain" value={original?.domain} copy={original?.domain} mono />
      <FieldRow label="Title" value={original?.title} />
      <FieldRow
        label="URL"
        value={original?.url || (original?.domain ? `https://${original.domain}` : undefined)}
        href={original?.url || (original?.domain ? `https://${original.domain}` : undefined)}
        copy={original?.url || (original?.domain ? `https://${original.domain}` : undefined)}
      />
      <FieldRow label="Site type" value={original?.site_type} />
      <FieldRow label="Infringement" value={original?.infringement_type} />
      <FieldRow label="Country" value={original?.country} />
      <FieldRow label="Language" value={original?.language} />
      <FieldRow label="Registrar" value={registrar} />
      <FieldRow label="Registrant" value={registrant} />
      <FieldRow label="Ranking" value={original?.ranking != null && original.ranking >= 0 ? original.ranking : undefined} />
      <FieldRow label="Description" value={original?.description} />
      <FieldRow label="Created" value={created} />
    </VStack>
  )
}

function EntityPopoverBody({ original }: { original: any }) {
  const locationParts = [original?.address, original?.city, original?.postcode, original?.country].filter(Boolean)
  return (
    <VStack align="stretch" spacing={0} divider={<Divider borderColor="gray.100" />}>
      <FieldRow label="Name" value={original?.name} copy={original?.name} />
      <FieldRow label="Type" value={original?.type} />
      <FieldRow label="Nickname" value={original?.nickname} />
      <FieldRow label="URL" value={original?.url} href={original?.url} copy={original?.url} />
      <FieldRow
        label="Email"
        value={original?.email}
        href={original?.email ? `mailto:${original.email}` : undefined}
        copy={original?.email}
      />
      <FieldRow
        label="Phone"
        value={original?.phone}
        href={original?.phone ? `tel:${original.phone}` : undefined}
        copy={original?.phone}
      />
      <FieldRow label="Location" value={locationParts.length > 0 ? locationParts.join(', ') : undefined} />
      <FieldRow label="Tax ID" value={original?.tax_id} copy={original?.tax_id} mono />
      <FieldRow label="Description" value={original?.description} />
      <FieldRow label="Created" value={formatDate(original?.created_at)} />
    </VStack>
  )
}

function ProductPopoverBody({ original }: { original: any }) {
  const photo = original?.photo || original?.image || original?.thumbnail
  const priceLabel =
    original?.price != null && Number(original.price) > 0
      ? `$${Number(original.price).toLocaleString()}`
      : undefined
  return (
    <VStack align="stretch" spacing={0} w="100%">
      {photo && (
        <Box mx={-4} mt={-4} mb={3} position="relative" overflow="hidden" borderTopRadius="lg">
          <Image src={photo} alt={original?.name} w="100%" maxH="160px" objectFit="cover" fallback={<Box h="0" />} />
          {priceLabel && (
            <Box
              position="absolute"
              top="10px"
              right="10px"
              bg="rgba(17, 24, 39, 0.85)"
              color="white"
              fontSize="12px"
              fontWeight={600}
              px={2.5}
              py={1}
              borderRadius="md"
              backdropFilter="blur(4px)"
            >
              {priceLabel}
            </Box>
          )}
        </Box>
      )}
      <VStack align="stretch" spacing={0} divider={<Divider borderColor="gray.100" />}>
        <FieldRow label="Name" value={original?.name} copy={original?.name} />
        <FieldRow label="Product type" value={original?.product_type} />
        {!photo && priceLabel && <FieldRow label="Price" value={priceLabel} copy={priceLabel} />}
        <FieldRow label="URL" value={original?.url} href={original?.url} copy={original?.url} />
        <FieldRow label="Seller" value={original?.seller} copy={original?.seller} mono />
        <FieldRow label="Website" value={original?.website} copy={original?.website} mono />
        <FieldRow label="Created" value={formatDate(original?.created_at)} />
      </VStack>
    </VStack>
  )
}

function SeizurePopoverBody({ original }: { original: any }) {
  const locationParts = [original?.address, original?.city, original?.postcode, original?.country].filter(Boolean)
  const valueLabel =
    original?.value != null && parseFloat(String(original.value)) > 0
      ? `$${parseFloat(String(original.value)).toLocaleString()}`
      : undefined
  return (
    <VStack align="stretch" spacing={0} divider={<Divider borderColor="gray.100" />}>
      <FieldRow label="Name" value={original?.name} copy={original?.name} />
      <FieldRow label="Value" value={valueLabel} copy={valueLabel} />
      <FieldRow label="Location" value={locationParts.length > 0 ? locationParts.join(', ') : undefined} />
      <FieldRow
        label="Seized"
        value={formatDate(original?.seized_at)}
        copy={original?.seized_at ? String(original.seized_at) : undefined}
      />
      <FieldRow label="Remarks" value={original?.remarks} />
      <FieldRow label="Created" value={formatDate(original?.created_at)} />
    </VStack>
  )
}

// Body for member-portal records (cases / sub-cases / contacts). Their schema
// is external/variable, so render whatever the portal payload actually carries
// (important keys first) rather than a fixed, guessed field list.
function MpPopoverBody({ original }: { original: any }) {
  return (
    <GenericFieldsBody
      original={original}
      priority={[
        'name', 'reference', 'case_number', 'number', 'type', 'case_type',
        'status', 'email', 'phone', 'address', 'city', 'country', 'brand',
        'member', 'url', 'link', 'created_at', 'updated_at',
      ]}
    />
  )
}

function ExpandButton({
  data,
  palette,
}: {
  data: CircleNodeData
  palette: { border: string; bg: string; icon: string; glow: string; ring: string }
}) {
  if (!data.onExpand) return null
  // Generic LINK nodes (unknown / external URL hits) don't have graph
  // relationships worth expanding — use Investigate online instead, which
  // searches for incoming + outgoing references to the link.
  if (data.kind === 'LINK') return null
  // Infrastructure nodes (platform/ISP/registrar/registrant) are shared across
  // many sellers; expanding them yields a useless hairball, not a focused
  // investigation. Hide expansion entirely.
  if (
    data.kind === 'PLATFORM' ||
    data.kind === 'ISP' ||
    data.kind === 'REGISTRAR' ||
    data.kind === 'REGISTRANT'
  ) {
    return null
  }
  const isExpanding = !!data.expanding
  const degreeRaw = (data.original as { degree?: number } | undefined)?.degree
  const hasDegree = typeof degreeRaw === 'number'
  const isIsolated = hasDegree && degreeRaw === 0
  return (
    <Box
      as="button"
      type="button"
      flexShrink={0}
      px="8px"
      h="22px"
      borderRadius="6px"
      fontSize="10px"
      fontWeight={700}
      letterSpacing="0.02em"
      color={isIsolated ? 'gray.500' : 'white'}
      display="inline-flex"
      alignItems="center"
      gap="5px"
      style={{
        background: isIsolated
          ? 'transparent'
          : isExpanding
            ? `${palette.border}aa`
            : palette.border,
        border: isIsolated ? '1px solid #e2e8f0' : 'none',
      }}
      cursor={isIsolated ? 'not-allowed' : isExpanding ? 'progress' : 'pointer'}
      aria-disabled={isIsolated || undefined}
      onClick={(e) => {
        e.stopPropagation()
        if (isExpanding || isIsolated) return
        data.onExpand?.()
      }}
      sx={{
        '@keyframes kraken-popover-spin': {
          from: { transform: 'rotate(0deg)' },
          to: { transform: 'rotate(360deg)' },
        },
        '&:hover': isIsolated ? {} : { opacity: isExpanding ? 1 : 0.9 },
      }}
    >
      {isExpanding ? (
        <Box as={Loader2} w="11px" h="11px" sx={{ animation: 'kraken-popover-spin 0.9s linear infinite' }} />
      ) : (
        <Box as={Expand} w="11px" h="11px" />
      )}
      {isExpanding ? 'Expanding…' : isIsolated ? 'Cannot expand' : 'Expand'}
    </Box>
  )
}

// The image a node can be reverse-searched on — a real product/profile PHOTO,
// not a full-page site screenshot. So it shows on: the searched-image anchor,
// item/product thumbnails, and social-post / reverse-image match photos — but
// NOT on site / fingerprint / generic-link nodes (those keep "Investigate
// online", whose `screenshot` is a page capture, not a photo to search).
const imageUrlForNode = (data: CircleNodeData): string => {
  const o = data.original as any
  const sk = String(o?.source_kind ?? '').toLowerCase()
  let cand: unknown = ''
  if (data.kind === 'IMAGE') cand = o?.url || data.url
  else if (data.kind === 'ITEM' || data.kind === 'PRODUCT') cand = o?.thumbnail || o?.image
  else if (sk.includes('social') || sk === 'reverse_image') cand = o?.screenshot || o?.image
  return typeof cand === 'string' ? cand : ''
}

// Reverse-image-search button — shown on any node that carries an image, in
// place of "Investigate online".
function ReverseImageButton({
  data,
  palette,
}: {
  data: CircleNodeData
  palette: { border: string; bg: string; icon: string; glow: string; ring: string }
}) {
  const onGo = data.onReverseImageSearch
  const img = imageUrlForNode(data)
  if (!onGo || !img) return null
  const isRunning = !!data.discovering
  return (
    <Box
      as="button"
      type="button"
      flexShrink={0}
      px="8px"
      h="22px"
      borderRadius="6px"
      fontSize="10px"
      fontWeight={700}
      letterSpacing="0.02em"
      color="white"
      display="inline-flex"
      alignItems="center"
      gap="5px"
      style={{ background: isRunning ? `${palette.border}aa` : palette.border, border: 'none' }}
      cursor={isRunning ? 'progress' : 'pointer'}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation()
        if (!isRunning) onGo(img)
      }}
      sx={{
        '@keyframes kraken-rimg-spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
        '&:hover': { opacity: isRunning ? 1 : 0.9 },
      }}
    >
      {isRunning ? (
        <Box as={Loader2} w="11px" h="11px" sx={{ animation: 'kraken-rimg-spin 0.9s linear infinite' }} />
      ) : (
        <Box as={ScanSearch} w="11px" h="11px" />
      )}
      Reverse image search
    </Box>
  )
}

const OSINT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const OSINT_PHONE_RE = /^\+?\d[\d\s()-]{6,}$/

// OSINT button for identifier nodes: an email → account-footprint check
// (check_email_accounts), a phone → carrier/region/line-type lookup (phone_osint).
// Detects the identifier from the node's value so it also covers LINK nodes that
// are actually an email/phone, not just the EMAIL/PHONE kinds. Sends the request
// as an Octopus chat prompt, mirroring the reverse-image button.
function IdentifierOsintButton({
  data,
  palette,
}: {
  data: CircleNodeData
  palette: { border: string; bg: string; icon: string; glow: string; ring: string }
}) {
  const o = data.original as { name?: unknown } | undefined
  const value = (typeof o?.name === 'string' ? o.name : data.fullTitle || '').trim()
  const isEmail = OSINT_EMAIL_RE.test(value)
  const isPhone = !isEmail && OSINT_PHONE_RE.test(value)
  const onGo = isEmail ? data.onInvestigateEmailOsint : isPhone ? data.onInvestigatePhoneOsint : undefined
  if (!onGo || !value) return null
  const kindLabel = isEmail ? 'email' : 'phone'
  return (
    <Box
      as="button"
      type="button"
      flexShrink={0}
      px="8px"
      h="22px"
      borderRadius="6px"
      fontSize="10px"
      fontWeight={700}
      letterSpacing="0.02em"
      color="white"
      display="inline-flex"
      alignItems="center"
      gap="5px"
      style={{ background: palette.border, border: 'none' }}
      cursor="pointer"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation()
        onGo(value)
      }}
      sx={{ '&:hover': { opacity: 0.9 } }}
    >
      <Box as={Radar} w="11px" h="11px" />
      Investigate {kindLabel} OSINT
    </Box>
  )
}

const OSINT_USERNAME_RE = /^@?[A-Za-z0-9._]{2,30}$/
const SOCIAL_HANDLE_HOSTS = ['instagram.com', 'tiktok.com', 'x.com', 'twitter.com', 'facebook.com']

// A value that ends in a dotted TLD (e.g. "ubuy.com.pr", "acme.co") is a domain,
// not a handle — it belongs to the domain-footprint button, never the ~700-site
// username sweep. `OSINT_USERNAME_RE` allows dots (real handles like "some.user"
// have them), so this guard is what keeps bare domains out of the username path.
const OSINT_LOOKS_LIKE_DOMAIN_RE = /\.[a-z]{2,}$/i

// Extract an @handle from a social profile URL, e.g.
// "https://www.instagram.com/some.user/" -> "some.user".
const usernameFromSocialUrl = (url: string): string => {
  try {
    const u = new URL(url)
    const host = u.hostname.replace(/^www\./, '')
    if (!SOCIAL_HANDLE_HOSTS.some((h) => host === h || host.endsWith(`.${h}`))) return ''
    const segment = u.pathname.split('/').filter(Boolean)[0] ?? ''
    return segment.replace(/^@/, '')
  } catch {
    return ''
  }
}

// Username-OSINT button: check where a handle/username exists across ~700
// sites (Sherlock-style sweep). Detects the username from a social profile
// URL, an @handle value, or (narrowly) a single-token seller/entity name.
// Sends the request as an Octopus chat prompt, mirroring the OSINT button.
function UsernameOsintButton({
  data,
  palette,
}: {
  data: CircleNodeData
  palette: { border: string; bg: string; icon: string; glow: string; ring: string }
}) {
  const o = data.original as { name?: unknown; url?: unknown } | undefined
  const rawValue = (typeof o?.name === 'string' ? o.name : data.fullTitle || '').trim()

  let username = ''
  const url = (typeof o?.url === 'string' ? o.url : data.url) || ''
  if (data.kind === 'SOCIAL' && url) {
    username = usernameFromSocialUrl(url)
  }
  // A bare @handle value — but NOT a domain (ubuy.com.pr) or an email.
  if (
    !username &&
    rawValue &&
    !OSINT_EMAIL_RE.test(rawValue) &&
    !OSINT_LOOKS_LIKE_DOMAIN_RE.test(rawValue) &&
    OSINT_USERNAME_RE.test(rawValue)
  ) {
    username = rawValue.replace(/^@/, '')
  }
  if (!username && (data.kind === 'SELLER' || data.kind === 'ENTITY') && rawValue) {
    // Only a single token (no spaces) reads as a plausible handle — multi-word
    // seller/entity names are too noisy to treat as usernames. Domain-looking
    // values are excluded above (they go to the domain-footprint button).
    if (!/\s/.test(rawValue) && !OSINT_LOOKS_LIKE_DOMAIN_RE.test(rawValue) && OSINT_USERNAME_RE.test(rawValue)) {
      username = rawValue.replace(/^@/, '')
    }
  }

  const onGo = data.onInvestigateUsernameOsint
  if (!onGo || !username) return null
  return (
    <Box
      as="button"
      type="button"
      flexShrink={0}
      px="8px"
      h="22px"
      borderRadius="6px"
      fontSize="10px"
      fontWeight={700}
      letterSpacing="0.02em"
      color="white"
      display="inline-flex"
      alignItems="center"
      gap="5px"
      style={{ background: palette.border, border: 'none' }}
      cursor="pointer"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation()
        onGo(username)
      }}
      sx={{ '&:hover': { opacity: 0.9 } }}
    >
      <Box as={Radar} w="11px" h="11px" />
      Investigate username OSINT
    </Box>
  )
}

// Extract a bare hostname (no "www.") from a URL string, or '' if invalid.
const hostnameFromUrl = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return ''
  }
}

// Domain-footprint button: subdomains + co-hosted sibling sites + look-alike
// domains for a domain-bearing node. Excludes infra kinds (a registrar/ISP/
// platform/registrant page has no domain footprint of its own worth mapping).
function DomainFootprintButton({
  data,
  palette,
}: {
  data: CircleNodeData
  palette: { border: string; bg: string; icon: string; glow: string; ring: string }
}) {
  if (
    data.kind === 'PLATFORM' ||
    data.kind === 'ISP' ||
    data.kind === 'REGISTRAR' ||
    data.kind === 'REGISTRANT'
  ) {
    return null
  }
  const o = data.original as { domain?: unknown; url?: unknown; name?: unknown } | undefined
  let domain = typeof o?.domain === 'string' ? o.domain : ''
  if (!domain) {
    const url = (typeof o?.url === 'string' ? o.url : data.url) || ''
    if (url) domain = hostnameFromUrl(url)
  }
  // Fall back to the node value/name when it is itself a bare domain — LINK nodes
  // (e.g. "www.ubuy.com.pr") carry the domain there, with no separate domain/url.
  if (!domain) {
    const val = (typeof o?.name === 'string' ? o.name : data.fullTitle || '').trim()
    if (val && !/\s/.test(val) && OSINT_LOOKS_LIKE_DOMAIN_RE.test(val)) domain = val
  }
  domain = domain.replace(/^www\./, '')

  const onGo = data.onDomainFootprint
  if (!onGo || !domain) return null
  return (
    <Box
      as="button"
      type="button"
      flexShrink={0}
      px="8px"
      h="22px"
      borderRadius="6px"
      fontSize="10px"
      fontWeight={700}
      letterSpacing="0.02em"
      color="white"
      display="inline-flex"
      alignItems="center"
      gap="5px"
      style={{ background: palette.border, border: 'none' }}
      cursor="pointer"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation()
        onGo(domain)
      }}
      sx={{ '&:hover': { opacity: 0.9 } }}
    >
      <Box as={Radar} w="11px" h="11px" />
      Domain footprint
    </Box>
  )
}

function DiscoverWebButton({
  data,
  palette,
}: {
  data: CircleNodeData
  palette: { border: string; bg: string; icon: string; glow: string; ring: string }
}) {
  if (!data.onDiscoverWeb) return null
  // Infrastructure nodes have no individual web presence worth investigating —
  // a registrar/platform/ISP/registrant page would mostly return the corporate
  // homepage of the vendor itself. Hide the button.
  if (
    data.kind === 'PLATFORM' ||
    data.kind === 'ISP' ||
    data.kind === 'REGISTRAR' ||
    data.kind === 'REGISTRANT'
  ) {
    return null
  }
  const isDiscovering = !!data.discovering
  const onDiscoverWeb = data.onDiscoverWeb
  const trigger = (
    <Box
      as="button"
      type="button"
      flexShrink={0}
      px="8px"
      h="22px"
      borderRadius="6px"
      fontSize="10px"
      fontWeight={700}
      letterSpacing="0.02em"
      color="white"
      display="inline-flex"
      alignItems="center"
      gap="5px"
      style={{
        background: isDiscovering ? `${palette.border}aa` : palette.border,
        border: 'none',
      }}
      cursor={isDiscovering ? 'progress' : 'pointer'}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation()
      }}
      sx={{
        '@keyframes kraken-discover-spin': {
          from: { transform: 'rotate(0deg)' },
          to: { transform: 'rotate(360deg)' },
        },
        '&:hover': { opacity: isDiscovering ? 1 : 0.9 },
      }}
    >
      {isDiscovering ? (
        <Box as={Loader2} w="11px" h="11px" sx={{ animation: 'kraken-discover-spin 0.9s linear infinite' }} />
      ) : (
        <Box as={Telescope} w="11px" h="11px" />
      )}
      {isDiscovering ? 'Investigating…' : 'Investigate online'}
    </Box>
  )

  return (
    <DiscoverWebPopover
      trigger={trigger}
      isRunning={isDiscovering}
      onStart={(opts) => onDiscoverWeb(opts)}
    />
  )
}

function ViewDetailsButton({
  data,
  palette,
}: {
  data: CircleNodeData
  palette: { border: string; bg: string; icon: string; glow: string; ring: string }
}) {
  if (!data.onNavigate) return null
  const label =
    data.kind === 'SELLER' ? 'View seller'
      : data.kind === 'ITEM' ? 'View item'
        : data.kind === 'SAW' ? 'View site'
          : data.kind === 'ENTITY' ? 'View entity'
            : data.kind === 'PRODUCT' ? 'View product'
              : data.kind === 'SEIZURE' ? 'View case'
                : 'View'
  return (
    <Box
      as="button"
      type="button"
      flexShrink={0}
      px="8px"
      h="22px"
      borderRadius="6px"
      fontSize="10px"
      fontWeight={700}
      letterSpacing="0.02em"
      color={palette.icon}
      display="inline-flex"
      alignItems="center"
      gap="4px"
      bg="white"
      border="1px solid"
      borderColor={`${palette.border}55`}
      cursor="pointer"
      onClick={(e) => {
        e.stopPropagation()
        data.onNavigate?.()
      }}
      sx={{ '&:hover': { background: palette.ring, borderColor: palette.border } }}
    >
      <Box as={ExternalLink} w="11px" h="11px" />
      {label}
    </Box>
  )
}

// "Member" chip shown beside the kind badge for member-case nodes (custom
// entities / products / seizures) — these are member-portal cases.
function MemberBadge({ kind, original }: { kind: CircleNodeData['kind']; original?: Record<string, unknown> }) {
  if (!MEMBER_CASE_KINDS.has(kind)) return null
  // Show the record's ACTUAL type (e.g. an entity's "person"/"company") rather
  // than a generic "Member" tag, which reads as a property of the record.
  const rawType =
    kind === 'ENTITY' ? original?.type :
    kind === 'PRODUCT' ? (original?.product_type ?? 'product') :
    kind === 'SEIZURE' ? 'seizure' : undefined
  const label = typeof rawType === 'string' && rawType.trim() ? rawType.trim() : String(kind).toLowerCase()
  return (
    <Badge
      fontSize="10px"
      fontWeight={600}
      textTransform="uppercase"
      letterSpacing="0.04em"
      bg="#c026d3"
      color="white"
      px={2}
      py="2px"
      borderRadius="full"
      display="inline-flex"
      alignItems="center"
      gap="4px"
    >
      <Users size={9} strokeWidth={2.6} />
      {label}
    </Badge>
  )
}

// "MP" chip shown beside the kind badge for member-portal records (cases /
// sub-cases / contacts) — green to match their unique node palette.
function MpBadge({ kind }: { kind: CircleNodeData['kind'] }) {
  if (!MP_CASE_KINDS.has(kind)) return null
  return (
    <Badge
      fontSize="10px"
      fontWeight={600}
      textTransform="uppercase"
      letterSpacing="0.04em"
      bg="#1e3a8a"
      color="white"
      px={2}
      py="2px"
      borderRadius="full"
      display="inline-flex"
      alignItems="center"
      gap="4px"
    >
      <Box as={ExternalLink} w="9px" h="9px" />
      MP
    </Badge>
  )
}

// Portal deep-link for member-portal records (cases/sub-cases/contacts).
// Custom entities/products/seizures use Ocean routes via ViewDetailsButton.
function MemberPortalButton({ data }: { data: CircleNodeData }) {
  if (!MP_CASE_KINDS.has(data.kind)) return null
  const href = mpDeepLink(data.kind, data.original?.id != null ? String(data.original.id) : undefined, data.original)
  return (
    <Box
      as="button"
      type="button"
      flexShrink={0}
      px="8px"
      h="22px"
      borderRadius="6px"
      fontSize="10px"
      fontWeight={700}
      letterSpacing="0.02em"
      color="#1e3a8a"
      display="inline-flex"
      alignItems="center"
      gap="4px"
      bg="white"
      border="1px solid"
      borderColor="#1e3a8a55"
      cursor="pointer"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation()
        window.open(href, '_blank', 'noopener,noreferrer')
      }}
      sx={{ '&:hover': { background: 'rgba(30,58,138,0.10)', borderColor: '#1e3a8a' } }}
    >
      <Box as={ExternalLink} w="11px" h="11px" />
      View in MP
    </Box>
  )
}

// Kinds that can be added as a custom entity. The "Add as entity" affordance
// (the floating "+" beside the node — see NodeAddEntityFab) shows only for
// these kinds, and only when the node carries a real Ocean record id (which is
// what gates data.onCreateEntity being wired in KrakenNetworkTree).
export const CREATE_ENTITY_KINDS = new Set<string>(['SELLER', 'ITEM', 'SAW', 'ENTITY', 'SEIZURE'])

// Floating "+" button anchored to the bottom-right of an eligible node. Replaces
// the old "Create entity" pill that lived inside the hover popover — surfaced
// directly on the node so it's reachable without opening the card. Opens the
// AddEntityModal ("Create custom link") for in-Ocean records. The AI-driven
// create-entity for discovered LINK nodes lives INSIDE the popover card
// (see NodeActionRow → CreateEntityWithAiButton), not on this "+".
// Always visible on eligible nodes; grows + brightens on hover.
function NodeAddEntityFab({
  data,
  palette,
}: {
  data: CircleNodeData
  palette: { border: string; bg: string; icon: string; glow: string; ring: string }
}) {
  if (!data.onCreateEntity || !CREATE_ENTITY_KINDS.has(data.kind)) return null
  const label = 'Create custom link'
  return (
    <Tooltip
      label={label}
      placement="bottom"
      hasArrow
      openDelay={200}
      bg="gray.900"
      color="white"
      fontSize="11px"
      fontWeight={600}
      px={2.5}
      py={1}
      borderRadius="8px"
      boxShadow="0 6px 18px -4px rgba(15, 23, 42, 0.35)"
    >
      <Box
        as="button"
        type="button"
        aria-label={label}
        position="absolute"
        zIndex={5}
        bottom="-8px"
        right="-8px"
        w="20px"
        h="20px"
        borderRadius="full"
        bg={palette.border}
        border="2px solid white"
        color="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
        cursor="pointer"
        boxShadow="0 2px 6px rgba(0,0,0,0.18)"
        transition="transform 150ms ease, box-shadow 150ms ease, background 150ms ease"
        transformOrigin="center"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
          data.onCreateEntity?.()
        }}
        sx={{
          '&:hover': {
            transform: 'scale(1.25)',
            background: palette.icon,
            boxShadow: `0 4px 12px ${palette.glow}`,
          },
          '&:active': { transform: 'scale(1.1)' },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: palette.border,
            outlineOffset: '2px',
          },
        }}
      >
        <Box as={Plus} w="12px" h="12px" strokeWidth={3} />
      </Box>
    </Tooltip>
  )
}

// Secondary action bar — sits beneath the title, left-aligned. Expand lives
// in the badges row at the top; this row carries the contextual actions
// (View detail page, Investigate web presence, Create entity with AI). It sits
// beneath the title so a long title never gets crammed against these pills.
// "Add as entity" no longer
// lives here — it's the floating NodeAddEntityFab on the node itself.
// "Create entity with AI" — pill INSIDE the popover card for a discovered LINK
// node not yet in Ocean. Fires the headless create-entity flow (scrape → reason
// type → create CustomEntity). Shows a spinner while running. This is distinct
// from the node's "+" ("Create custom link"); it lives in the card, matching
// DiscoverWebButton's style.
function CreateEntityWithAiButton({
  data,
  palette,
}: {
  data: CircleNodeData
  palette: { border: string; bg: string; icon: string; glow: string; ring: string }
}) {
  if (!data.onAiCreateEntity) return null
  const busy = !!data.creatingEntity
  const onCreate = data.onAiCreateEntity
  return (
    <Box
      as="button"
      type="button"
      flexShrink={0}
      px="8px"
      h="22px"
      borderRadius="6px"
      fontSize="10px"
      fontWeight={700}
      letterSpacing="0.02em"
      color="white"
      display="inline-flex"
      alignItems="center"
      gap="5px"
      style={{
        background: busy ? `${palette.border}aa` : palette.border,
        border: 'none',
      }}
      cursor={busy ? 'progress' : 'pointer'}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation()
        if (busy) return
        onCreate?.()
      }}
      sx={{
        '@keyframes kraken-aicreate-spin': {
          from: { transform: 'rotate(0deg)' },
          to: { transform: 'rotate(360deg)' },
        },
        '&:hover': { opacity: busy ? 1 : 0.9 },
      }}
    >
      {busy ? (
        <Box as={Loader2} w="11px" h="11px" sx={{ animation: 'kraken-aicreate-spin 0.9s linear infinite' }} />
      ) : (
        <Box as={Sparkles} w="11px" h="11px" />
      )}
      {busy ? 'Creating…' : 'Create manual entity'}
    </Box>
  )
}

function NodeActionRow({
  data,
  palette,
}: {
  data: CircleNodeData
  palette: { border: string; bg: string; icon: string; glow: string; ring: string }
}) {
  if (!data.onNavigate && !data.onDiscoverWeb && !data.onReverseImageSearch && !data.onInvestigateEmailOsint && !data.onInvestigatePhoneOsint && !data.onInvestigateUsernameOsint && !data.onDomainFootprint && !data.onAiCreateEntity && !MP_CASE_KINDS.has(data.kind)) return null
  return (
    <Flex
      gap="6px"
      flexWrap="wrap"
      justify="flex-start"
      align="center"
      rowGap="6px"
    >
      <ViewDetailsButton data={data} palette={palette} />
      <ReverseImageButton data={data} palette={palette} />
      <IdentifierOsintButton data={data} palette={palette} />
      <UsernameOsintButton data={data} palette={palette} />
      <DomainFootprintButton data={data} palette={palette} />
      <DiscoverWebButton data={data} palette={palette} />
      <CreateEntityWithAiButton data={data} palette={palette} />
      <MemberPortalButton data={data} />
    </Flex>
  )
}

function NodeTooltipContent({ data }: { data: CircleNodeData }) {
  const original: any = data.original
  const isGroup = data.kind === 'GROUP'
  // Groups borrow their member kind's colour/label so the tooltip says what
  // kind of bundle it is ("ITEM GROUP") rather than a generic "GROUP".
  const palette = KIND_COLORS[isGroup ? (data.memberKind ?? 'LINK') : data.kind] ?? KIND_COLORS.LINK
  const ctLabel = data.kind === 'CONTACT' ? contactTypeLabel(original) : undefined
  const kindLabel = isGroup ? `${data.memberKind ?? ''} group`.trim() : ctLabel ? `Contact · ${ctLabel}` : (MP_KIND_LABEL[data.kind] ?? data.kind)
  const isItem = data.kind === 'ITEM'
  const hasThumbnail = isItem && Boolean(original?.thumbnail || original?.image)

  return (
    <Box
      bg="white"
      borderRadius="14px"
      overflow="hidden"
      minW="340px"
      maxW="410px"
      border="1px solid"
      borderColor="blackAlpha.100"
      boxShadow="0 12px 32px -8px rgba(15, 23, 42, 0.28), 0 4px 10px -4px rgba(15, 23, 42, 0.12)"
    >
      {/* Firecrawl page screenshot preview (discovered web matches). */}
      {original?.screenshot && (
        <Box position="relative" borderBottom="1px solid" borderColor="gray.100" bg="gray.50">
          <Image
            src={original.screenshot}
            alt="Page preview"
            display="block"
            w="100%"
            maxH="180px"
            objectFit="cover"
            objectPosition="top"
            fallback={<Skeleton w="100%" h="120px" />}
          />
          <Badge
            position="absolute"
            top="6px"
            left="6px"
            fontSize="9px"
            fontWeight={600}
            textTransform="uppercase"
            letterSpacing="0.04em"
            bg="blackAlpha.700"
            color="white"
            px={2}
            py="2px"
            borderRadius="full"
          >
            Live preview
          </Badge>
        </Box>
      )}

      {/* Header (only shown when no thumbnail to avoid stacking two banners) */}
      {!hasThumbnail && (
        <Box
          px={4}
          pt={4}
          pb={3}
          bg={`linear-gradient(180deg, ${palette.ring} 0%, rgba(255,255,255,0) 100%)`}
          borderBottom="1px solid"
          borderColor="gray.100"
        >
          {/* Title row: kind-coloured icon chip + name, with expand pinned right. */}
          <HStack spacing={2.5} align="flex-start">
            <Flex
              flexShrink={0}
              w="34px"
              h="34px"
              borderRadius="10px"
              align="center"
              justify="center"
              bg="white"
              color={palette.icon}
              border="1px solid"
              borderColor={palette.border + '33'}
              boxShadow={`0 2px 6px ${palette.glow}`}
            >
              {isGroup ? <KindGlyph kind={data.memberKind ?? 'LINK'} size={18} /> : <KindGlyph kind={data.kind} size={18} fingerprinted={(data.original?.source as string) === 'fingerprint'} />}
            </Flex>
            <Box flex="1" minW={0}>
              <Text fontSize="14px" fontWeight={700} color="gray.900" noOfLines={2} wordBreak="break-word" lineHeight="1.25">
                {data.fullTitle}
              </Text>
              <HStack spacing={1.5} mt={1} align="center" flexWrap="wrap" rowGap="4px">
                <Text
                  fontSize="10px"
                  fontWeight={700}
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                  color={palette.icon}
                >
                  {kindLabel}
                </Text>
                <MemberBadge kind={data.kind} original={original as Record<string, unknown> | undefined} />
                <StatusBadge status={original?.status as string | undefined} />
              </HStack>
            </Box>
            <ExpandButton data={data} palette={palette} />
          </HStack>
          {(data.onNavigate || data.onDiscoverWeb || data.onAiCreateEntity || data.onInvestigateUsernameOsint || data.onDomainFootprint || MP_CASE_KINDS.has(data.kind)) && (
            <Box mt={2.5}>
              <NodeActionRow data={data} palette={palette} />
            </Box>
          )}
        </Box>
      )}

      <Box px={4} pt={hasThumbnail ? 0 : 3} pb={3}>
        {hasThumbnail && (
          <>
            <HStack spacing={2} pt={3} pb={2} align="center">
              <Badge
                fontSize="10px"
                fontWeight={600}
                textTransform="uppercase"
                letterSpacing="0.04em"
                bg="white"
                color={palette.icon}
                border="1px solid"
                borderColor={palette.border + '55'}
                px={2}
                py="2px"
                borderRadius="full"
              >
                {kindLabel}
              </Badge>
              <MemberBadge kind={data.kind} original={original as Record<string, unknown> | undefined} />
              <MpBadge kind={data.kind} />
              <StatusBadge status={original?.status as string | undefined} />
              <Box flex="1" />
              <ExpandButton data={data} palette={palette} />
            </HStack>
            <Box pb={2}>
              <NodeActionRow data={data} palette={palette} />
            </Box>
          </>
        )}

        {isItem && <ItemPopoverBody original={original} />}

        {isGroup && (
          <VStack align="stretch" spacing={0} divider={<Divider borderColor="gray.100" />}>
            <FieldRow
              label="Group of"
              value={`${data.count ?? ''} ${GROUP_NOUN[data.memberKind ?? 'LINK'] ?? 'nodes'}`.trim()}
            />
            {data.relation && <FieldRow label="Relation" value={data.relation} />}
            <FieldRow label="Action" value="Click the node to expand" />
          </VStack>
        )}

        {data.kind === 'SELLER' && original && <SellerPopoverBody original={original} />}

        {(data.kind === 'SAW' || data.kind === 'SOCIAL') && original && (
          <SitePopoverBody original={original} />
        )}

        {data.kind === 'PLATFORM' && original && (
          <VStack align="stretch" spacing={0} divider={<Divider borderColor="gray.100" />}>
            <FieldRow label="Name" value={original.name || original.title} copy={(original.name || original.title) as string} />
            <FieldRow label="Domain" value={original.domain as string} copy={original.domain as string} mono />
            <FieldRow
              label="URL"
              value={original.url || (original.domain ? `https://${original.domain}` : undefined)}
              href={(original.url || (original.domain ? `https://${original.domain}` : undefined)) as string | undefined}
              copy={(original.url || (original.domain ? `https://${original.domain}` : undefined)) as string | undefined}
            />
            <FieldRow label="Country" value={original.country as string} />
            <FieldRow label="Created" value={formatDate(original.created_at)} />
          </VStack>
        )}

        {data.kind === 'ENTITY' && original && <EntityPopoverBody original={original} />}
        {data.kind === 'PRODUCT' && original && <ProductPopoverBody original={original} />}
        {data.kind === 'SEIZURE' && original && <SeizurePopoverBody original={original} />}

        {MP_CASE_KINDS.has(data.kind) && original && <MpPopoverBody original={original} />}

        {(data.kind === 'EMAIL' || data.kind === 'PHONE' || data.kind === 'LINK') && original && (
          <VStack align="stretch" spacing={0} divider={<Divider borderColor="gray.100" />}>
            {original.url && (
              <FieldRow label="URL" value={original.url} href={original.url} copy={original.url as string} />
            )}
            {original.name && <FieldRow label="Value" value={original.name} copy={original.name as string} />}
          </VStack>
        )}

        {/* Surface any remaining payload fields the curated body above doesn't
            cover, so no available data stays hidden. (MP records render their
            whole body generically, so they're excluded here.) */}
        {!isGroup && !MP_CASE_KINDS.has(data.kind) && original && (
          <GenericFieldsBody
            original={original}
            skipKeys={curatedBodyKeys(data.kind)}
            caption="More details"
            leadDivider
          />
        )}
      </Box>
    </Box>
  )
}

// Resolve the glyph for a given node kind. Shared by ordinary nodes and by
// collapsed GROUP bundles, which render their member kind's icon (e.g. items)
// rather than the bundle domain's favicon — a bundle of items should read as
// items, not as the site they were found on.
function KindGlyph({ kind, size = 22, fingerprinted = false }: { kind: CircleNodeData['kind']; size?: number; fingerprinted?: boolean }) {
  // Infra discovered by the fingerprint tool (ISP / registrar not already in the
  // graph) gets a distinct fingerprint glyph so it reads as fingerprint-sourced
  // rather than a native graph vertex. Reused (native) infra keeps its own icon.
  if (fingerprinted) return <Fingerprint size={size} strokeWidth={1.8} />
  if (kind === 'ENTITY') return <Building2 size={size} strokeWidth={1.8} />
  if (kind === 'PRODUCT') return <Package size={size} strokeWidth={1.8} />
  if (kind === 'SEIZURE') return <ShieldAlert size={size} strokeWidth={1.8} />
  if (kind === 'CASE' || kind === 'SUBCASE') return <Gavel size={size} strokeWidth={1.8} />
  if (kind === 'CONTACT') return <Contact size={size} strokeWidth={1.8} />
  if (kind === 'REGISTRAR') return <Building size={size} strokeWidth={1.8} />
  if (kind === 'REGISTRANT') return <Building2 size={size} strokeWidth={1.8} />
  if (kind === 'ISP') return <Server size={size} strokeWidth={1.8} />
  if (kind === 'IMAGE') return <ImageIcon size={size} strokeWidth={1.8} />
  // The Phone glyph's artwork is centred on (10.5, 10), not (12, 12), so a
  // 0 0 24 24 viewBox renders it offset up-left. Frame it on its own bbox.
  if (kind === 'PHONE')
    return (
      <Icon viewBox="0 0 21 20" w={`${size}px`} h={`${size}px`} fill="none">
        <Phone />
      </Icon>
    )
  return (
    <Icon viewBox="0 0 24 24" w={`${size}px`} h={`${size}px`} fill="none">
      {kind === 'SELLER' ? (
        <SellersIcon />
      ) : kind === 'ITEM' ? (
        <PackageBox />
      ) : kind === 'EMAIL' ? (
        <Email />
      ) : kind === 'PHONE' ? (
        <Phone />
      ) : kind === 'PLATFORM' ? (
        <PlatformLayout />
      ) : (
        <Globe />
      )}
    </Icon>
  )
}

// Human, plural nouns for GROUP tooltips ("Group of 8 items").
const GROUP_NOUN: Record<string, string> = {
  ITEM: 'items',
  SELLER: 'sellers',
  SAW: 'sites',
  SOCIAL: 'social profiles',
  LINK: 'links',
  EMAIL: 'emails',
  PHONE: 'phone numbers',
  PLATFORM: 'platforms',
  ENTITY: 'entities',
  PRODUCT: 'products',
  SEIZURE: 'seizures',
  CASE: 'cases',
  SUBCASE: 'sub-cases',
  CONTACT: 'contacts',
  REGISTRAR: 'registrars',
  REGISTRANT: 'registrants',
  ISP: 'ISPs',
}

export function CircleNode({ data, selected }: { data: CircleNodeData; selected?: boolean }) {
  const isExport = typeof data.exportNum === 'number'
  const [isHovered, setIsHovered] = useState(false)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const openHover = () => {
    if (isExport) return
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    setIsHovered(true)
  }
  const scheduleClose = () => {
    if (isExport) return
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    closeTimerRef.current = setTimeout(() => setIsHovered(false), 220)
  }
  const glow = data.active || selected
  const isGroup = data.kind === 'GROUP'
  // GROUP bundles borrow their member kind's colour so they read as "more of these".
  const palette = KIND_COLORS[isGroup ? (data.memberKind ?? 'LINK') : data.kind] ?? KIND_COLORS.LINK

  // For link-like nodes (bare URLs, SAWs, discovered web results) show the
  // site's favicon instead of a generic globe, falling back to the globe if it
  // fails to load.
  const [faviconError, setFaviconError] = useState(false)
  const faviconDomain = (() => {
    const raw = data.url || (data.kind === 'LINK' ? data.fullTitle : '')
    if (!raw) return ''
    try {
      return new URL(raw.startsWith('http') ? raw : `https://${raw}`).hostname.replace(/^www\./, '')
    } catch {
      return ''
    }
  })()
  // Web/social GROUP bundles (LINK/SAW/SOCIAL members, all same domain) show the
  // domain's favicon — e.g. a bundle of Instagram posts reads as Instagram, not a
  // generic globe. Non-web bundles (items, etc.) keep their member kind's glyph.
  // NB: only a COLLAPSED group shows the favicon — an EXPANDED group must keep
  // its Shrink collapse control, so it's excluded here (else the collapse icon
  // renders as the bundle's favicon, e.g. an Instagram glyph).
  const showFavicon =
    !faviconError &&
    !!faviconDomain &&
    (data.kind === 'LINK' || data.kind === 'SAW' || data.kind === 'SOCIAL' ||
      (data.kind === 'GROUP' && data.collapsed &&
        (data.memberKind === 'LINK' || data.memberKind === 'SAW' || data.memberKind === 'SOCIAL')))
  const faviconUrl = faviconDomain
    ? `https://www.google.com/s2/favicons?domain=${faviconDomain}&sz=64`
    : ''

  const getBorderStyle = () => {
    return glow
      ? `2px solid ${palette.border}`
      : `2px solid ${palette.border}66`
  }

  const getBoxShadow = () => {
    if (glow) return `0 8px 22px ${palette.glow}, 0 0 0 6px ${palette.ring}`
    if (isHovered) return `0 6px 18px ${palette.glow}`
    return '0 4px 12px rgba(0,0,0,0.08)'
  }

  const getColor = () => palette.icon
  const getBg = () => glow ? palette.bg : '#fff'

  return (
    <>
      {!isExport && (
        <NodeToolbar isVisible={isHovered} position={Position.Top} offset={6}>
          <Box
            pb="10px"
            zIndex={9999}
            onMouseEnter={openHover}
            onMouseLeave={scheduleClose}
            onClick={(e) => e.stopPropagation()}
            sx={{
              '@keyframes kraken-popover-in': {
                from: { opacity: 0, transform: 'translateY(4px) scale(0.97)' },
                to: { opacity: 1, transform: 'translateY(0) scale(1)' },
              },
              // Snappy 160ms entrance so the card reads as instant, not laggy.
              animation: 'kraken-popover-in 160ms cubic-bezier(0.16, 1, 0.3, 1)',
              transformOrigin: 'bottom center',
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            <NodeTooltipContent data={data} />
          </Box>
        </NodeToolbar>
      )}

      <div
        style={{
          // The connection box must be exactly the circle so React Flow anchors
          // edges/handles to the circle's centre. The label is positioned
          // absolutely below (see below) so it doesn't inflate the box.
          position: 'relative',
          width: DEFAULT_NODE_SIZE,
          height: DEFAULT_NODE_SIZE,
          pointerEvents: 'all',
          cursor: isGroup ? 'pointer' : 'default',
        }}
        className="nopan nodrag"
        onClick={isGroup ? () => data.onToggleGroup?.() : undefined}
      >
        {/* Hover handlers live on the circle wrapper (not the outer node box) so
            the "+" Add-as-entity FAB — a sibling below — is outside the popover
            trigger region: hovering it never opens the card, and moving back to
            the circle reopens it via a normal mouseenter (no timer race). */}
        <Box
          position="relative"
          display="inline-block"
          onMouseEnter={openHover}
          onMouseLeave={scheduleClose}
        >
          {/* Invisible hit-area padding around the circle so the hover popover
              triggers as the pointer approaches the node, not only on a precise
              hit of the small circle — kills the "takes a while to show" feel. */}
          {!isExport && (
            <Box position="absolute" inset="-12px" borderRadius="full" zIndex={1} pointerEvents="all" />
          )}
          {/* Bundle nodes get two offset "ghost" circles behind to read as a stack. */}
          {isGroup && data.collapsed && [10, 5].map((off) => (
            <div
              key={off}
              style={{
                position: 'absolute',
                inset: 0,
                transform: `translate(${off}px, ${off}px)`,
                borderRadius: '999px',
                background: '#fff',
                border: `2px solid ${palette.border}55`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                zIndex: 0,
              }}
            />
          ))}
          <div
            style={{
              position: 'relative',
              zIndex: 2,
              width: DEFAULT_NODE_SIZE,
              height: DEFAULT_NODE_SIZE,
              borderRadius: '999px',
              background: getBg(),
              border: getBorderStyle(),
              boxShadow: getBoxShadow(),
              display: 'grid',
              placeItems: 'center',
              color: getColor(),
              userSelect: 'none',
              transition: 'box-shadow 150ms ease, transform 150ms ease, border-color 150ms ease, background 150ms ease',
              opacity: data.expanding || data.discovering ? 0.7 : 1,
            }}
          >
            {showFavicon ? (
              <Image
                src={faviconUrl}
                alt=""
                boxSize="22px"
                borderRadius="4px"
                objectFit="contain"
                fallback={<Skeleton boxSize="22px" borderRadius="4px" />}
                onError={() => setFaviconError(true)}
              />
            ) : isGroup ? (
              data.collapsed ? (
                // Collapsed bundle shows the member kind's icon (e.g. items);
                // the "+N" count lives in the corner badge below.
                <KindGlyph kind={data.memberKind ?? 'LINK'} />
              ) : (
                <Shrink size={22} strokeWidth={2} color={palette.icon} />
              )
            ) : (
              <KindGlyph kind={data.kind} fingerprinted={(data.original?.source as string) === 'fingerprint'} />
            )}
          </div>
          {(data.expanding || data.discovering) && (
            <Box
              position="absolute"
              inset="-6px"
              borderRadius="full"
              border="2px solid transparent"
              borderTopColor={palette.border}
              borderRightColor={palette.border}
              pointerEvents="none"
              sx={{
                '@keyframes kraken-node-spin': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' },
                },
                animation: 'kraken-node-spin 0.9s linear infinite',
              }}
            />
          )}
          {isGroup && data.collapsed && (
            <Box
              position="absolute"
              zIndex={3}
              top="-6px"
              right="-8px"
              minW="20px"
              h="18px"
              px="5px"
              borderRadius="full"
              bg={palette.border}
              border="2px solid white"
              color="white"
              fontSize="10px"
              fontWeight={800}
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow="0 2px 6px rgba(0,0,0,0.18)"
            >
              {`+${data.count ?? ''}`}
            </Box>
          )}
          {!isExport && !isGroup && MEMBER_CASE_KINDS.has(data.kind) && (
            // Member-case marker — these nodes are member-portal cases.
            <Box
              position="absolute"
              zIndex={3}
              top="-6px"
              left="-8px"
              w="18px"
              h="18px"
              borderRadius="full"
              bg="#c026d3"
              border="2px solid white"
              color="white"
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow="0 2px 6px rgba(0,0,0,0.18)"
              title="Member case"
            >
              <Users size={9} strokeWidth={2.6} />
            </Box>
          )}
          {isExport && (
            <Box
              position="absolute"
              zIndex={3}
              top="-8px"
              left="-8px"
              minW="20px"
              h="20px"
              px="4px"
              borderRadius="full"
              bg={palette.border}
              border="2px solid white"
              color="white"
              fontSize={data.exportNum! > 99 ? '8px' : data.exportNum! > 9 ? '9px' : '10px'}
              fontWeight={800}
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow="0 2px 6px rgba(0,0,0,0.18)"
            >
              {data.exportNum}
            </Box>
          )}
          {!isGroup && MP_CASE_KINDS.has(data.kind) && (
            // Member-portal marker — these nodes are records pulled from the
            // member portal (cases / sub-cases / contacts). Dark-blue "external
            // connection" badge, pulled slightly toward the node centre.
            <Box
              position="absolute"
              zIndex={3}
              top="-3px"
              left="-4px"
              w="22px"
              h="22px"
              borderRadius="full"
              bg="#1e3a8a"
              border="2px solid white"
              color="white"
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow="0 2px 6px rgba(0,0,0,0.18)"
              title="Member portal record"
            >
              <ExternalLink size={12} strokeWidth={2.4} />
            </Box>
          )}
          {Boolean(data.original?.verified) && (
            <Box
              position="absolute"
              top="-2px"
              right="-2px"
              w="14px"
              h="14px"
              borderRadius="full"
              bg="green.500"
              border="2px solid white"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                <path d="M1.5 4L3 5.5L6.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Box>
          )}
        </Box>

        {/* "Add as entity" — floating "+" on eligible nodes (not in export
            renders, not on group bundles). Rendered as a SIBLING of the circle
            wrapper (which owns the hover popover), so hovering the "+" never
            triggers the card. Reuses data.onCreateEntity. */}
        {!isExport && !isGroup && (
          <NodeAddEntityFab data={data} palette={palette} />
        )}

        {!data.clustered && !isGroup && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 160,
              textAlign: 'center',
              fontSize: 12,
              lineHeight: 1.2,
              color: '#334155',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
            {data.fullTitle || data.label}
            {data.kind === 'CONTACT' && contactTypeLabel(data.original) && (
              <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                {contactTypeLabel(data.original)}
              </div>
            )}
          </div>
        )}

        {/* Four bidirectional handles so edges can connect to whichever side
            of the node is closest to the connected node. Each cardinal point
            gets both a source and a target handle so an edge in either
            direction can pick the same side. KrakenNetworkTree assigns the
            specific sourceHandle / targetHandle for every edge after layout. */}
        {(['top', 'right', 'bottom', 'left'] as const).map((dir) => {
          const pos =
            dir === 'top' ? Position.Top
            : dir === 'right' ? Position.Right
            : dir === 'bottom' ? Position.Bottom
            : Position.Left
          return (
            <Box key={dir}>
              <Handle type="source" position={pos} id={`s-${dir}`} style={{ opacity: 0, pointerEvents: 'none' }} />
              <Handle type="target" position={pos} id={`t-${dir}`} style={{ opacity: 0, pointerEvents: 'none' }} />
            </Box>
          )
        })}
      </div>
    </>
  )
}

// Custom entities/products/seizures are member cases — records that belong to
// member brands and surface in the member portal. They share one visual
// identity (fuchsia family + member badge) and a portal deep-link.
export const MEMBER_CASE_KINDS = new Set<string>(['ENTITY', 'PRODUCT', 'SEIZURE'])
export const MEMBER_PORTAL_URL = 'https://member.react.org'

// Member-portal "pulled" records arrive as nodes whose ids are prefixed by their
// record kind: `case_<id>` (parent case), `subcase_<id>` (sub-case) and
// `contact_<id>` (contact). They share a single emerald MP identity and a
// per-record deep link back into the member portal.
export const MP_CASE_KINDS = new Set<string>(['CASE', 'SUBCASE', 'CONTACT'])

// Human labels for the MP kinds (badge / popover header).
export const MP_KIND_LABEL: Record<string, string> = {
  CASE: 'Case',
  SUBCASE: 'Sub-case',
  CONTACT: 'Contact',
}

// Contact type integer → human label (from member portal Contact vertex).
const CONTACT_TYPE_LABEL: Record<number, string> = {
  1: 'Person',
  2: 'Company',
}
const contactTypeLabel = (original?: any): string | undefined => {
  if (!original?.type) return undefined
  const t = typeof original.type === 'number' ? original.type : parseInt(original.type, 10)
  return CONTACT_TYPE_LABEL[t]
}

// Build the member-portal deep link for an MP node. The portal uses HashRouter
// so all routes live under `/#/member/...`.
//
// Cases:    /#/member/cases/<caseId>/
// Subcases: /#/member/cases/<parentCaseId>/<subcaseId>  (needs parent_case_id from vertex)
// Contacts: /#/member/contacts/<contactId>/
export function mpDeepLink(kind: string, id?: string, original?: Record<string, any>): string {
  const base = `${MEMBER_PORTAL_URL}/#/member`
  if (!id) return base

  if (kind === 'CASE') {
    const rawId = id.startsWith('case_') ? id.slice('case_'.length) : id
    return rawId ? `${base}/cases/${rawId}/` : base
  }
  if (kind === 'SUBCASE') {
    const rawId = id.startsWith('subcase_') ? id.slice('subcase_'.length) : id
    const parentId = original?.parent_case_id
    if (rawId && parentId) return `${base}/cases/${parentId}/${rawId}`
    // Fall back to cases list if parent is unknown
    return `${base}/cases`
  }
  if (kind === 'CONTACT') {
    const rawId = id.startsWith('contact_') ? id.slice('contact_'.length) : id
    return rawId ? `${base}/contacts/${rawId}/` : base
  }
  return base
}

// Per-entity-type node styling
export const KIND_COLORS: Record<string, { border: string; bg: string; icon: string; glow: string; ring: string }> = {
  SELLER: { border: '#7c3aed', bg: '#faf5ff', icon: '#4c1d95', glow: 'rgba(124,58,237,0.30)', ring: 'rgba(124,58,237,0.12)' },
  ITEM:   { border: '#2563eb', bg: '#eff6ff', icon: '#1e3a8a', glow: 'rgba(37,99,235,0.30)',  ring: 'rgba(37,99,235,0.12)'  },
  SAW:    { border: '#0d9488', bg: '#f0fdfa', icon: '#134e4a', glow: 'rgba(13,148,136,0.30)', ring: 'rgba(13,148,136,0.12)' },
  SOCIAL: { border: '#db2777', bg: '#fdf2f8', icon: '#831843', glow: 'rgba(219,39,119,0.30)', ring: 'rgba(219,39,119,0.12)' },
  LINK:   { border: '#64748b', bg: '#f8fafc', icon: '#334155', glow: 'rgba(100,116,139,0.20)', ring: 'rgba(100,116,139,0.08)' },
  IMAGE:  { border: '#14b8a6', bg: '#f0fdfa', icon: '#0f766e', glow: 'rgba(20,184,166,0.30)', ring: 'rgba(20,184,166,0.12)' },
  EMAIL:  { border: '#4f46e5', bg: '#eef2ff', icon: '#3730a3', glow: 'rgba(79,70,229,0.30)',  ring: 'rgba(79,70,229,0.12)'  },
  PLATFORM:{ border: '#9333ea', bg: '#faf5ff', icon: '#581c87', glow: 'rgba(147,51,234,0.30)', ring: 'rgba(147,51,234,0.12)' },
  PHONE:  { border: '#0891b2', bg: '#ecfeff', icon: '#155e75', glow: 'rgba(8,145,178,0.30)',  ring: 'rgba(8,145,178,0.12)'  },
  // Member-case family (custom entities/products/seizures) — one unmistakable
  // fuchsia range so member-portal cases read as "member" at a glance.
  ENTITY: { border: '#c026d3', bg: '#fdf4ff', icon: '#86198f', glow: 'rgba(192,38,211,0.30)',  ring: 'rgba(192,38,211,0.12)'  },
  PRODUCT:{ border: '#d946ef', bg: '#fdf4ff', icon: '#a21caf', glow: 'rgba(217,70,239,0.30)',  ring: 'rgba(217,70,239,0.12)'  },
  SEIZURE:{ border: '#a21caf', bg: '#fdf4ff', icon: '#701a75', glow: 'rgba(162,28,175,0.30)',  ring: 'rgba(162,28,175,0.12)'  },
  REGISTRAR:{ border: '#9f1239', bg: '#fff1f2', icon: '#881337', glow: 'rgba(159,18,57,0.28)',  ring: 'rgba(159,18,57,0.10)'  },
  REGISTRANT:{border: '#b45309', bg: '#fffbeb', icon: '#78350f', glow: 'rgba(180,83,9,0.28)',   ring: 'rgba(180,83,9,0.10)'  },
  ISP:    { border: '#0e7490', bg: '#ecfeff', icon: '#164e63', glow: 'rgba(14,116,144,0.28)',  ring: 'rgba(14,116,144,0.10)' },
  // Member-portal data family (cases / sub-cases / contacts pulled from the
  // member portal) — one unmistakable dark-blue range so MP records read as
  // "MP / external" at a glance and stand apart from the fuchsia member-case
  // family (and from the brighter ITEM blue).
  CASE:    { border: '#1e3a8a', bg: '#eff6ff', icon: '#172554', glow: 'rgba(30,58,138,0.30)',  ring: 'rgba(30,58,138,0.12)'  },
  SUBCASE: { border: '#1e40af', bg: '#eff6ff', icon: '#1e3a8a', glow: 'rgba(30,64,175,0.30)',  ring: 'rgba(30,64,175,0.12)'  },
  CONTACT: { border: '#1d4ed8', bg: '#eff6ff', icon: '#1e3a8a', glow: 'rgba(29,78,216,0.30)',  ring: 'rgba(29,78,216,0.12)'  },
}

// Edge type color map — blue and purple shades
const EDGE_COLORS: Record<string, string> = {
  similar_phone: '#7c3aed',    // violet-600
  similar_email: '#4f46e5',    // indigo-600
  similar_name: '#a855f7',     // purple-500
  same_name: '#a855f7',        // purple-500
  same_url: '#6d28d9',         // violet-700
  similar_address: '#6366f1',  // indigo-500
  sold_by: '#818cf8',         // indigo-400
  listed_on: '#8b5cf6',       // violet-500
  links_to: '#6d28d9',        // violet-700
  social_profile: '#db2777',  // pink-600 — matches the SOCIAL node palette
  discovery: '#64748b',       // slate-500 — fallback for discovery edges
  same_registrant: '#4338ca', // indigo-700
  same_ip: '#c084fc',         // purple-400
  same_postcode: '#93c5fd',   // blue-300
}

const getEdgeColor = (label?: string): string => {
  return EDGE_COLORS[label || ''] || '#a78bfa'
}

// Visual palette for `discovery` edges, keyed off `source_kind`. Color +
// short text make the kind scannable on a busy graph. Server enum is closed —
// anything unrecognised falls back to `other`.
type DiscoveryKind =
  | 'social_profile'
  | 'social_post'
  | 'marketplace_seller'
  | 'marketplace_listing'
  | 'official_site'
  | 'news_article'
  | 'reverse_image'
  | 'search_image'
  | 'other'

const DISCOVERY_KIND_STYLE: Record<DiscoveryKind, { color: string; label: string }> = {
  social_profile:     { color: '#ec4899', label: 'social profile' }, // pink-500
  social_post:        { color: '#f472b6', label: 'social post'    }, // pink-400
  marketplace_seller: { color: '#f59e0b', label: 'seller'         }, // amber-500
  marketplace_listing:{ color: '#d97706', label: 'listing'        }, // amber-600
  official_site:      { color: '#059669', label: 'official site'  }, // emerald-600
  news_article:       { color: '#0ea5e9', label: 'news'           }, // sky-500
  reverse_image:      { color: '#14b8a6', label: 'same image'     }, // teal-500
  search_image:       { color: '#0d9488', label: 'searched image' }, // teal-600
  other:              { color: '#64748b', label: 'link'           }, // slate-500
}

const getDiscoveryStyle = (kind?: string) => {
  // Normalise separators ('social post' → 'social_post') so both the apify
  // ('social post') and discovery ('social_profile') spellings map cleanly.
  const base = (kind || 'other').trim().toLowerCase().replace(/\s+/g, '_')
  // Map old unsplit 'marketplace' to the new seller variant.
  const normalized = base === 'marketplace' ? 'marketplace_seller' : base
  const k = normalized as DiscoveryKind
  return DISCOVERY_KIND_STYLE[k] ?? DISCOVERY_KIND_STYLE.other
}

/**
 * Map a weight (1-10) to a color gradient:
 * 1-3: green (low), 4-5: amber (medium), 6-7: orange (high), 8-10: red (critical)
 */
export const getWeightColor = (weight: number): string => {
  if (weight <= 3) return '#22c55e'  // green-500
  if (weight <= 5) return '#eab308'  // yellow-500
  if (weight <= 7) return '#f97316'  // orange-500
  return '#ef4444'                    // red-500
}

/**
 * Get edge color: use weight-based coloring when weight data is available,
 * fall back to edge-type color for structural edges.
 */
const getEdgeDisplayColor = (
  label: string,
  score?: number,
  weight?: number
): string => {
  const similarityEdges = [
    'similar_phone', 'similar_email', 'similar_name',
    'similar_address', 'similar_platform_seller_id',
    'same_registrant', 'same_ip', 'same_postcode',
  ]

  if (similarityEdges.includes(label)) {
    const w = weight ?? (score != null ? Math.round(score * 10) : 5)
    return getWeightColor(w)
  }

  return EDGE_COLORS[label as keyof typeof EDGE_COLORS] || '#94a3b8'
}

// Short human-readable labels for edge types
export const EDGE_SHORT_LABELS: Record<string, string> = {
  // Raw edge labels
  sold_by: 'sold by',
  listed_on: 'listed on',
  on_platform: 'on platform',
  links_to: 'links to',
  social_profile: 'social media',
  discovery: 'discovery',
  linked_to: 'manual link',
  registered_by: 'registered by',
  hosted_by: 'hosted by',
  part_of: 'part of',
  has_stakeholder: 'stakeholder',
  has_brand: 'brand',
  has_email: 'email',
  has_phone: 'phone',
  in_local_area: 'in area',
  // Synthetic peer edges from hub collapse
  same_email: 'shares email',
  same_phone: 'shares phone',
  same_name: 'shares name',
  same_url: 'shares URL',
  same_local_area: 'same area',
  same_registrant: 'same registrant',
  same_ip: 'same IP',
  same_postcode: 'same postcode',
  // Identifier-similarity edges (not currently synced but reserved)
  similar_phone: 'similar phone',
  similar_email: 'similar email',
  similar_name: 'similar name',
  similar_address: 'similar address',
  similar_seller: 'similar seller',
  similar_platform_seller_id: 'similar platform id',
}

const getEdgeShortLabel = (label?: string, edgeData?: Record<string, any>): string => {
  if (!label) return ''
  // has_stakeholder edges carry the specific role (debtor, creditor, …)
  if (label === 'has_stakeholder' && edgeData?.stakeholder_type) {
    return String(edgeData.stakeholder_type)
  }
  if (EDGE_SHORT_LABELS[label]) return EDGE_SHORT_LABELS[label]
  // Fallback: turn snake_case into "snake case" so any unmapped edge label
  // still renders without underscores.
  return label.replace(/_/g, ' ')
}

// Edge tooltip content component
function EdgeTooltipContent({
  edge,
}: {
  edge: {
    label: string
    score?: number
    weight?: number
    source_kind?: string
    confidence?: number
    match_type?: string
    reasoning?: string
    stakeholder_type?: string
    quantity?: string
    product_type?: string
  }
}) {
  // OSINT finds — web-discovery ('discovery') AND social finds (source_kind like
  // 'social post' / 'social_profile') — carry a real confidence + explanation.
  // Everything else is a structural Ocean-DB edge whose `weight` means
  // connection strength (a DIFFERENT purpose), so we never surface it as a %.
  const sourceKind = (edge.source_kind || '').toLowerCase()
  const isSocial = sourceKind.includes('social')
  const isOsint = edge.label === 'discovery' || isSocial
  const kindStyle = isOsint ? getDiscoveryStyle(edge.source_kind) : null
  const color = isOsint
    ? (kindStyle?.color ?? '#64748b')
    : getEdgeDisplayColor(edge.label, edge.score, edge.weight)
  // Confidence %: from an explicit confidence, else a real match score — never
  // from `weight` (that's the structural strength used by the slider, not a
  // match probability). Social finds ALWAYS show a % — fall back to a sensible
  // default for edges saved before the backend started stamping confidence.
  const pct =
    typeof edge.confidence === 'number'
      ? Math.round(edge.confidence)
      : isOsint && typeof edge.score === 'number'
        ? Math.round(edge.score * 100)
        : isSocial
          ? 75
          : null
  // Social finds always carry an explanation too, with a generic fallback so
  // older persisted edges still describe themselves.
  const reasoning =
    edge.reasoning ||
    (isSocial
      ? 'Surfaced for this entity via social search — a post/profile likely tied to the target.'
      : '')
  return (
    <VStack align="start" p={3} spacing={2} maxW="320px" bg="white">
      <HStack spacing={2}>
        <Badge size="sm" bg={color} color="white" px={2} py={0.5} borderRadius="md">
          {kindStyle?.label ?? edge.label}
        </Badge>
        {isOsint && pct !== null && (
          <Text fontSize="xs" color="gray.700" fontWeight="700">
            {pct}% confidence
          </Text>
        )}
      </HStack>
      {isOsint && edge.match_type && (
        <Text fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.05em" fontWeight={600}>
          {edge.match_type}
        </Text>
      )}
      {isOsint && reasoning && (
        <Text fontSize="12px" color="gray.700" lineHeight="1.4">
          {reasoning}
        </Text>
      )}
      {edge.stakeholder_type && (
        <Text fontSize="11px" color="gray.500" fontWeight={600}>
          Role: {edge.stakeholder_type}
        </Text>
      )}
      {edge.product_type && (
        <Text fontSize="11px" color="gray.500" fontWeight={600}>
          Product type: {edge.product_type}
        </Text>
      )}
      {edge.quantity && (
        <Text fontSize="11px" color="gray.500" fontWeight={600}>
          Quantity: {edge.quantity}
        </Text>
      )}
    </VStack>
  )
}

// Lighten a hex color by mixing with white
function lightenColor(hex: string, amount: number): string {
  const h = hex.replace('#', '')
  const r = parseInt(h.substring(0, 2), 16)
  const g = parseInt(h.substring(2, 4), 16)
  const b = parseInt(h.substring(4, 6), 16)
  const lr = Math.round(r + (255 - r) * amount)
  const lg = Math.round(g + (255 - g) * amount)
  const lb = Math.round(b + (255 - b) * amount)
  return `#${lr.toString(16).padStart(2, '0')}${lg.toString(16).padStart(2, '0')}${lb.toString(16).padStart(2, '0')}`
}

// Custom edge: gradient, no arrows, with label on hover
export function KrakenEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  style,
  ...props
}: EdgeProps) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  const edgeLabel = (data?.edgeData?.label as string) ?? ''
  const isDiscovery = edgeLabel === 'discovery'
  const discoveryStyle = isDiscovery
    ? getDiscoveryStyle(data?.edgeData?.source_kind as string | undefined)
    : null
  const edgeColor = (style?.stroke || (isDiscovery
    ? (discoveryStyle?.color ?? '#64748b')
    : getEdgeDisplayColor(
        edgeLabel,
        data?.edgeData?.score as number | undefined,
        data?.edgeData?.weight as number | undefined,
      ))) as string
  const shortLabel = isDiscovery
    ? (discoveryStyle?.label ?? 'link')
    : getEdgeShortLabel(edgeLabel, data?.edgeData as Record<string, any> | undefined)
  const sw = style?.strokeWidth ?? 1.5
  const op = style?.opacity ?? 0.5
  const gradientId = `edge-gradient-${id}`

  return (
    <>
      <defs>
        <linearGradient id={gradientId} x1={sourceX} y1={sourceY} x2={targetX} y2={targetY} gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={edgeColor} stopOpacity={op as number * 0.4} />
          <stop offset="50%" stopColor={edgeColor} stopOpacity={op as number} />
          <stop offset="100%" stopColor={lightenColor(edgeColor, 0.5)} stopOpacity={op as number * 0.3} />
        </linearGradient>
      </defs>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          ...style,
          stroke: `url(#${gradientId})`,
          strokeWidth: sw,
          opacity: 1,
          strokeDasharray: '6 4',
        }}
        {...props}
      />
      {/* Hover zone + label pill — invisible wide hit area, pill shows on hover */}
      <foreignObject
        width={isDiscovery ? 120 : 80}
        height={24}
        x={labelX - (isDiscovery ? 60 : 40)}
        y={labelY - 12}
        className="edgebutton-foreignobject"
        requiredExtensions="http://www.w3.org/1999/xhtml"
      >
        <Tooltip
          label={<EdgeTooltipContent edge={(data?.edgeData as Parameters<typeof EdgeTooltipContent>[0]['edge']) || { label: '' }} />}
          bg="white"
          color="black"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          p={0}
          placement="top"
          hasArrow
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'default',
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '8px',
                padding: '2px 8px',
                fontSize: '10px',
                fontWeight: 700,
                color: edgeColor as string,
                whiteSpace: 'nowrap',
                lineHeight: '18px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
              }}
            >
              {shortLabel}
            </div>
          </div>
        </Tooltip>
      </foreignObject>
    </>
  )
}

export const nodeTypes = { circle: CircleNode }
export const edgeTypes = { kraken: KrakenEdge }

export function radialTreeLayout(
  nodes: Node[],
  edges: Edge[],
  rootId?: string,
  opts?: {
    radiusStep?: number
    centerX?: number
    centerY?: number
  }
): Node[] {
  if (!nodes.length) return nodes

  const centerX = opts?.centerX ?? 0
  const centerY = opts?.centerY ?? 0
  const NODE_SPACING = 160 // space between nodes within a cluster

  // --- Step 1: Find root ---
  const nodeSet = new Set(nodes.map((n) => n.id))

  let root = rootId
  if (!root || !nodeSet.has(root)) {
    const activeNode = nodes.find((n) => (n.data as CircleNodeData)?.active)
    root = activeNode?.id || nodes[0].id
  }

  // --- Fast path: small graphs get a clean hub-and-spoke layout ---
  // The cluster-based algorithm below over-engineers tiny 1-hop fan-outs
  // (3–5 nodes) into a squashed triangle. For small N we put the root in the
  // centre and spread its direct neighbours evenly on a single ring; anything
  // not connected to the root falls onto an outer ring nearest the neighbour
  // it does share an edge with.
  if (nodes.length <= 8) {
    const posMap = new Map<string, { x: number; y: number }>()
    posMap.set(root, { x: centerX, y: centerY })

    const adjacent = new Map<string, Set<string>>()
    for (const n of nodes) adjacent.set(n.id, new Set())
    for (const e of edges) {
      if (!e?.source || !e?.target) continue
      adjacent.get(e.source)?.add(e.target)
      adjacent.get(e.target)?.add(e.source)
    }
    const rootNeighbours: string[] = []
    for (const id of adjacent.get(root) ?? []) {
      if (id !== root) rootNeighbours.push(id)
    }

    // Tighter ring for very few neighbours so the network feels intentional
    // rather than scattered. Scales up gently as N grows.
    const ringRadius = Math.max(260, 120 + rootNeighbours.length * 46)

    // Spread neighbours across a "fan" of arc above the root. Two evenly-
    // spaced points around a full circle would land collinear with the root
    // (an ugly straight line), so for N ≤ 5 we constrain to a fan of 60° per
    // gap (60° for 2, 120° for 3, …) centered on the top. Once there are 6+
    // neighbours we open up to the full circle.
    const N = rootNeighbours.length
    const baseAngle = -Math.PI / 2 // top
    rootNeighbours.forEach((id, i) => {
      let angle: number
      if (N <= 1) {
        angle = baseAngle
      } else if (N >= 6) {
        angle = baseAngle + (2 * Math.PI * i) / N
      } else {
        const arcSpread = (Math.PI / 3) * (N - 1) // 60° per gap between neighbours
        angle = baseAngle - arcSpread / 2 + (arcSpread * i) / (N - 1)
      }
      posMap.set(id, {
        x: centerX + ringRadius * Math.cos(angle),
        y: centerY + ringRadius * Math.sin(angle),
      })
    })

    const outerRadius = ringRadius + 160
    let outerIdx = 0
    // Group unplaced nodes by their shared neighbour so siblings sharing the
    // same parent get fanned around that parent instead of stacking on top of
    // each other at the same angle.
    const bySharedNeighbour = new Map<string, string[]>()
    const noSharedNeighbour: string[] = []
    for (const n of nodes) {
      if (posMap.has(n.id)) continue
      const sharedNeighbour = [...(adjacent.get(n.id) ?? [])].find((id) => posMap.has(id) && id !== root)
      if (sharedNeighbour) {
        if (!bySharedNeighbour.has(sharedNeighbour)) bySharedNeighbour.set(sharedNeighbour, [])
        bySharedNeighbour.get(sharedNeighbour)!.push(n.id)
      } else {
        noSharedNeighbour.push(n.id)
      }
    }

    // Place children of each shared neighbour fanned across a small arc
    // centered on the neighbour's bearing from the root. One child: dead-on;
    // two children: ±18°; N children: spread over (N-1) × ~22° (cap 110°).
    for (const [parentId, childIds] of bySharedNeighbour) {
      const base = posMap.get(parentId)!
      const baseAngle = Math.atan2(base.y - centerY, base.x - centerX)
      const N = childIds.length
      const arcSpread = N === 1 ? 0 : Math.min(Math.PI * 0.6, (N - 1) * (Math.PI / 8))
      childIds.forEach((id, i) => {
        const angle = N === 1 ? baseAngle : baseAngle - arcSpread / 2 + (arcSpread * i) / (N - 1)
        posMap.set(id, {
          x: centerX + outerRadius * Math.cos(angle),
          y: centerY + outerRadius * Math.sin(angle),
        })
      })
    }

    for (const id of noSharedNeighbour) {
      const angle = -Math.PI / 2 + (2 * Math.PI * outerIdx) / Math.max(nodes.length, 1)
      posMap.set(id, {
        x: centerX + outerRadius * Math.cos(angle),
        y: centerY + outerRadius * Math.sin(angle),
      })
      outerIdx++
    }

    return nodes.map((n) => ({
      ...n,
      position: posMap.get(n.id) ?? { x: centerX, y: centerY },
    }))
  }

  // --- Radial tree layout ---
  // Build a spanning tree from the root via BFS, then place each node on a ring
  // whose radius grows with its depth, at an angle inside the angular "sector"
  // its subtree owns. Deeper layers fan out *underneath* their parent and the
  // whole graph reads as a tree radiating from the root — instead of edge-type
  // blobs stacked on top of one another. This is what keeps multi-layer,
  // heavily-nested graphs legible.
  const radiusStep = opts?.radiusStep ?? 240

  const adj = new Map<string, string[]>()
  for (const n of nodes) adj.set(n.id, [])
  for (const e of edges) {
    if (!e?.source || !e?.target) continue
    if (!nodeSet.has(e.source) || !nodeSet.has(e.target)) continue
    adj.get(e.source)!.push(e.target)
    adj.get(e.target)!.push(e.source)
  }

  // BFS spanning tree: the first edge to reach a node wins, so every node gets a
  // single parent and a depth (shortest hop distance from the root).
  const depthById = new Map<string, number>([[root, 0]])
  const childrenById = new Map<string, string[]>()
  for (const n of nodes) childrenById.set(n.id, [])
  const visitOrder: string[] = [root]
  const queue: string[] = [root]
  while (queue.length) {
    const u = queue.shift()!
    for (const v of adj.get(u) ?? []) {
      if (depthById.has(v)) continue
      depthById.set(v, (depthById.get(u) ?? 0) + 1)
      childrenById.get(u)!.push(v)
      visitOrder.push(v)
      queue.push(v)
    }
  }

  // Nodes unreachable from the root (disconnected fragments) still need a home —
  // hang them directly off the root so they ring the first layer.
  for (const n of nodes) {
    if (depthById.has(n.id)) continue
    depthById.set(n.id, 1)
    childrenById.get(root)!.push(n.id)
    visitOrder.push(n.id)
  }

  // Leaf weight of each subtree (a leaf counts as 1). Walking visitOrder in
  // reverse is a valid post-order because a parent always precedes its children
  // in BFS order.
  const leafCount = new Map<string, number>()
  for (let i = visitOrder.length - 1; i >= 0; i--) {
    const id = visitOrder[i]
    const kids = childrenById.get(id) ?? []
    leafCount.set(
      id,
      kids.length ? kids.reduce((s, c) => s + (leafCount.get(c) ?? 1), 0) : 1,
    )
  }
  // Ring radius per depth, built cumulatively from the centre outward. Each ring
  // sits at least `radiusStep` beyond the previous one, and is pushed further out
  // when that depth holds many nodes so its circumference can seat them all at
  // NODE_SPACING. This both makes the tree extend outwards and stops a busy layer
  // (e.g. dozens of direct children) from piling up near the centre.
  let maxDepth = 0
  const nodesAtDepth: number[] = []
  for (const d of depthById.values()) {
    if (d > maxDepth) maxDepth = d
    nodesAtDepth[d] = (nodesAtDepth[d] ?? 0) + 1
  }
  const radiusAt: number[] = [0]
  for (let d = 1; d <= maxDepth; d++) {
    const circumferenceFit = ((nodesAtDepth[d] ?? 1) * NODE_SPACING) / (2 * Math.PI)
    radiusAt[d] = Math.max(radiusAt[d - 1] + radiusStep, circumferenceFit)
  }
  const ringRadius = (d: number): number => (d <= 0 ? 0 : radiusAt[d] ?? d * radiusStep)

  // Give each node the angular sector it owns, split among its children in
  // proportion to their leaf weight. A node sits at the centre of its sector so
  // every parent stays visually centred over the children beneath it.
  const angleById = new Map<string, number>()
  const assignAngles = (id: string, a0: number, a1: number) => {
    angleById.set(id, (a0 + a1) / 2)
    const kids = childrenById.get(id) ?? []
    if (!kids.length) return
    const own = leafCount.get(id) ?? 1
    let a = a0
    for (const c of kids) {
      const slice = ((a1 - a0) * (leafCount.get(c) ?? 1)) / own
      assignAngles(c, a, a + slice)
      a += slice
    }
  }
  // Sweep a full turn around the root, starting from the top (-90°).
  assignAngles(root, -Math.PI / 2, -Math.PI / 2 + 2 * Math.PI)

  const posMap = new Map<string, { x: number; y: number }>()
  for (const n of nodes) {
    const d = depthById.get(n.id) ?? 0
    if (d === 0) {
      posMap.set(n.id, { x: centerX, y: centerY })
      continue
    }
    const r = ringRadius(d)
    const ang = angleById.get(n.id) ?? -Math.PI / 2
    posMap.set(n.id, {
      x: centerX + r * Math.cos(ang),
      y: centerY + r * Math.sin(ang),
    })
  }

  return nodes.map((n) => ({
    ...n,
    position: posMap.get(n.id) ?? { x: centerX, y: centerY },
  }))
}

// Social media domain patterns
const SOCIAL_DOMAINS = [
  'instagram.com', 'facebook.com', 'tiktok.com', 'twitter.com', 'x.com',
  'linkedin.com', 'youtube.com', 'pinterest.com', 'reddit.com', 'tumblr.com',
  'vk.com', 'weibo.com', 'wechat.com', 'telegram.org', 't.me',
]

const isSocialMediaNode = (node: KrakenNode): boolean => {
  const v = node.url || node.domain || ''
  const url = String(Array.isArray(v) ? v[0] ?? '' : v).toLowerCase()
  return SOCIAL_DOMAINS.some((d) => url.includes(d))
}

// Map Kraken label to internal kind
const mapLabelToKind = (label: KrakenNodeLabel, node?: KrakenNode): CircleNodeData['kind'] => {
  // Member-portal records are identified by their id prefix (`case_`,
  // `subcase_`, `contact_`). Check the most specific prefix first so `subcase_`
  // is never swallowed by a `case_` test. These take precedence over the label
  // switch, which would otherwise drop them to a generic LINK node.
  const nodeId = node ? String(node.id ?? '') : ''
  if (nodeId.startsWith('subcase_')) return 'SUBCASE'
  if (nodeId.startsWith('case_')) return 'CASE'
  if (nodeId.startsWith('contact_')) return 'CONTACT'
  // A social-platform domain renders a *site* or discovered link as a SOCIAL
  // node (Instagram/X/YouTube/Facebook/LinkedIn as social profiles rather than
  // generic sites). But a first-class Ocean record keeps its own type even when
  // its URL is a social domain — a Seller with an Instagram storefront is still
  // a SELLER, an Item is still an ITEM, and a Platform (e.g. Instagram itself,
  // "on platform") is still a PLATFORM. Only generic sites (Saw) / discovered
  // links get reclassified to SOCIAL.
  const keepsOwnType =
    label === 'Seller' ||
    label === 'Item' ||
    label === 'Platform' ||
    label === 'CustomEntity' ||
    label === 'CustomProduct' ||
    label === 'CustomSeizure'
  if (node && !keepsOwnType && isSocialMediaNode(node)) return 'SOCIAL'
  // The reverse-image "searched image" anchor node.
  if ((label as string) === 'IMAGE') return 'IMAGE'
  switch (label) {
    case 'Item':
      return 'ITEM'
    case 'Seller':
      return 'SELLER'
    case 'Saw':
      return 'SAW'
    case 'Email':
      return 'EMAIL'
    case 'Phone':
      return 'PHONE'
    case 'Platform':
      return 'PLATFORM'
    case 'CustomEntity':
      return 'ENTITY'
    case 'CustomProduct':
      return 'PRODUCT'
    case 'CustomSeizure':
      return 'SEIZURE'
    case 'Registrar':
      return 'REGISTRAR'
    case 'Registrant':
      return 'REGISTRANT'
    case 'Isp':
      return 'ISP'
    default:
      return 'LINK'
  }
}

// Transform Kraken response to ReactFlow nodes/edges
const hostnameOf = (raw?: string): string => {
  if (!raw) return ''
  try {
    return new URL(raw.startsWith('http') ? raw : `https://${raw}`)
      .hostname.replace(/^www\./, '')
      .toLowerCase()
  } catch {
    return String(raw).replace(/^www\./, '').toLowerCase()
  }
}

// Is this node the index/homepage of a site (root path, no deep path)?
const isIndexPage = (n: { url?: string; domain?: string }): boolean => {
  const v = n.url || n.domain || ''
  const raw = String(Array.isArray(v) ? v[0] ?? '' : v)
  if (!raw) return true
  try {
    const path = new URL(raw.startsWith('http') ? raw : `https://${raw}`).pathname
    return path === '' || path === '/'
  } catch {
    return true
  }
}

export const transformKrakenResponse = (data: KrakenGraphResponse) => {
  const nodeIdSet = new Set<string>()

  // When the graph is rooted on a website (a standalone-site investigation), the
  // web-presence discovery often returns that same site's own homepage as a
  // match — which would draw the site as a connection to itself. Drop the
  // same-domain index node so a standalone site never links to its own index.
  const rootNode = data.nodes.find((n) => String(n.id) === String(data.root))
  const rootKind = rootNode ? mapLabelToKind(rootNode.label, rootNode) : undefined
  const rootIsSite = rootKind === 'SAW' || rootKind === 'SOCIAL'
  const rootHost = rootIsSite ? hostnameOf(rootNode?.domain || rootNode?.url) : ''
  const selfIndexIds = new Set<string>()
  if (rootHost) {
    for (const n of data.nodes) {
      if (String(n.id) === String(data.root)) continue
      if (hostnameOf(n.domain || n.url) === rootHost && isIndexPage(n)) {
        selfIndexIds.add(String(n.id))
      }
    }
  }

  const rfNodes: Node<CircleNodeData>[] = data.nodes
    .map((n) => {
      const idStr = String(n.id)
      if (!idStr) return null as unknown as Node<CircleNodeData>
      // Skip the standalone site's own index node (see selfIndexIds above).
      // Leaving it out of nodeIdSet also drops any edge that referenced it.
      if (selfIndexIds.has(idStr)) return null as unknown as Node<CircleNodeData>

      const kind = mapLabelToKind(n.label, n)
      const rawLabel = n.title || n.name || n.domain || n.url || idStr.slice(0, 6)
      // Pretty-print phone numbers (e.g. +905445444438 → +90 544 544 4438).
      const fullLabel = kind === 'PHONE' ? formatPhoneLabel(rawLabel) : rawLabel
      // Display the readable name; the label box (maxWidth 160px) ellipsis-truncates
      // anything too long, so we keep ~24 chars rather than a hard 6-char cut.
      const shortLabel = shortenTitle(fullLabel, 24)

      nodeIdSet.add(idStr)
      return {
        id: idStr,
        type: 'circle',
        position: { x: 0, y: 0 },
        data: {
          label: shortLabel,
          fullTitle: fullLabel,
          firstLetter: '',
          kind,
          original: n,
          url: n.url,
          active: n.id === data.root,
        },
        selectable: true,
        draggable: true,
      } as Node<CircleNodeData>
    })
    .filter(Boolean)

  // Web-discovery links that merely "link to" a social-platform node read better
  // as a social-media relationship than a generic "links to". NOTE: `linked_to`
  // is deliberately excluded — it's the user's own MANUAL link type (custom
  // entity → seller/item/site), so it must keep its identity and never be
  // recoloured as social media just because an endpoint has a social presence.
  const socialIds = new Set(
    data.nodes.filter((n) => isSocialMediaNode(n)).map((n) => String(n.id)),
  )
  const GENERIC_LINK_LABELS = new Set(['links_to', 'link'])

  const rfEdges: Edge[] = data.edges
    .map((e) => {
      const src = String(e.from_id)
      const dst = String(e.to_id)
      if (!src || !dst) return null
      if (!nodeIdSet.has(src) || !nodeIdSet.has(dst)) return null
      // Relabel generic links pointing at a social profile to "social media".
      const touchesSocial = socialIds.has(src) || socialIds.has(dst)
      const label = touchesSocial && GENERIC_LINK_LABELS.has(e.label) ? 'social_profile' : e.label
      e = label === e.label ? e : ({ ...e, label } as typeof e)
      // `discovery` edges color by `source_kind` instead of the edge label.
      const color = e.label === 'discovery'
        ? getDiscoveryStyle((e as { source_kind?: string }).source_kind).color
        : getEdgeDisplayColor(e.label, e.score, e.weight)
      // Normalise to 0-1: prefer weight (1-10 scale), fall back to score (0-1), default 0.5
      const rawWeight = typeof e.weight === 'number' ? e.weight / 10 : typeof e.score === 'number' ? e.score : 0.5
      const score = Math.max(0, Math.min(1, rawWeight))
      // Map score to stroke width: 1px (low) to 4px (critical)
      const strokeWidth = 1 + score * 3
      // Map score to opacity: 0.30 (low) to 0.80 (high)
      const opacity = 0.30 + score * 0.50
      return {
        id: `${src}->${dst}-${e.label}`,
        source: src,
        target: dst,
        type: 'kraken',
        animated: false,
        style: { stroke: color, strokeWidth, opacity },
        data: { edgeData: e, score },
      } as Edge
    })
    .filter(Boolean) as Edge[]

  return { nodes: rfNodes, edges: rfEdges }
}

// Bundle a node's same-relation, same-DOMAIN leaf neighbours into one
// collapsible GROUP node when there are more than `threshold` of them.
// Collapsed: anchor → group ("+N"). Expanded: anchor → group → members.
// The graph stays a tree, so the radial layout keeps working.
export function collapseRelations(
  nodes: Node<CircleNodeData>[],
  edges: Edge[],
  expandedGroups: Set<string>,
  threshold = 5,
): { nodes: Node<CircleNodeData>[]; edges: Edge[] } {
  const degree = new Map<string, number>()
  for (const e of edges) {
    degree.set(e.source, (degree.get(e.source) ?? 0) + 1)
    degree.set(e.target, (degree.get(e.target) ?? 0) + 1)
  }
  const nodeById = new Map(nodes.map((n) => [n.id, n]))
  const relOf = (e: Edge) => String((e.data as { edgeData?: { label?: string } } | undefined)?.edgeData?.label ?? 'related')
  const domainOf = (n?: Node<CircleNodeData>): string => {
    if (!n) return ''
    const d = n.data as CircleNodeData
    const o = d.original as { domain?: unknown; url?: string } | undefined
    // `domain` can arrive non-string (e.g. a Gremlin valueMap array, or an
    // enriched/discovered node) — guard so a non-string value falls through to
    // the URL-based derivation instead of throwing `domain.replace is not a function`.
    if (typeof o?.domain === 'string' && o.domain) return o.domain.replace(/^www\./, '')
    const raw = d.url || o?.url || ''
    if (!raw) return ''
    try {
      return new URL(raw.startsWith('http') ? raw : `https://${raw}`).hostname.replace(/^www\./, '')
    } catch {
      return ''
    }
  }

  type Member = { id: string; edge: Edge; memberIsSource: boolean }
  const groups = new Map<string, { anchorId: string; relation: string; domain: string; members: Member[] }>()
  for (const e of edges) {
    const sDeg = degree.get(e.source) ?? 0
    const tDeg = degree.get(e.target) ?? 0
    let memberId: string
    let anchorId: string
    let memberIsSource: boolean
    if (sDeg === 1 && tDeg > 1) {
      memberId = e.source; anchorId = e.target; memberIsSource = true
    } else if (tDeg === 1 && sDeg > 1) {
      memberId = e.target; anchorId = e.source; memberIsSource = false
    } else {
      continue
    }
    const dom = domainOf(nodeById.get(memberId))
    if (!dom) continue // grouping requires a shared domain
    const rel = relOf(e)
    const key = `${anchorId}::${rel}::${dom}`
    const g = groups.get(key) ?? { anchorId, relation: rel, domain: dom, members: [] }
    g.members.push({ id: memberId, edge: e, memberIsSource })
    groups.set(key, g)
  }

  const dropNodes = new Set<string>()
  const dropEdges = new Set<string>()
  const addNodes: Node<CircleNodeData>[] = []
  const addEdges: Edge[] = []
  for (const [key, g] of groups) {
    if (g.members.length <= threshold) continue
    const groupId = `group::${key}`
    const expanded = expandedGroups.has(groupId)
    const memberKind = (nodeById.get(g.members[0].id)?.data as CircleNodeData)?.kind ?? 'LINK'
    const dirMemberIsSource = g.members[0].memberIsSource
    addNodes.push({
      id: groupId,
      type: 'circle',
      position: { x: 0, y: 0 },
      data: {
        kind: 'GROUP',
        label: String(g.members.length),
        fullTitle: `${g.members.length} · ${g.domain}`,
        firstLetter: '',
        url: `https://${g.domain}`,
        count: g.members.length,
        relation: g.relation,
        memberKind,
        collapsed: !expanded,
      },
      selectable: true,
      draggable: false,
    } as Node<CircleNodeData>)
    addEdges.push({
      id: `e-${groupId}`,
      source: dirMemberIsSource ? groupId : g.anchorId,
      target: dirMemberIsSource ? g.anchorId : groupId,
      type: 'kraken',
      data: { edgeData: { label: g.relation }, score: 0.6 },
      style: { stroke: '#a78bfa', strokeWidth: 2, opacity: 0.7 },
    } as Edge)
    for (const m of g.members) {
      dropEdges.add(m.edge.id)
      if (expanded) {
        addEdges.push({
          id: `e-${groupId}-${m.id}`,
          source: m.memberIsSource ? m.id : groupId,
          target: m.memberIsSource ? groupId : m.id,
          type: 'kraken',
          data: { edgeData: { label: g.relation }, score: 0.4 },
          style: { stroke: '#c4b5fd', strokeWidth: 1, opacity: 0.5 },
        } as Edge)
      } else {
        dropNodes.add(m.id)
      }
    }
  }

  return {
    nodes: nodes.filter((n) => !dropNodes.has(n.id)).concat(addNodes),
    edges: edges.filter((e) => !dropEdges.has(e.id)).concat(addEdges),
  }
}
